import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { TEBOX_DEFAULT_LOGO_URL } from '@boubou/tebox-copy';
import { AlertTriangle } from 'lucide-react';
import { ICON_STROKE } from '../../constants/icon-stroke';
import {
  resolveBookingBrandColors,
  type BookingBrandColors,
} from '../../utils/booking-brand-colors';
import {
  bookingBrandFontFamilyVar,
  bookingBrandInfoChromeStyles,
  teboxDesignSystem,
  useBookingTebox,
} from '../../styles/tebox-booking';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppState } from '../../contexts/AppStateContext';
import { useIsMobile } from '../ui/use-mobile';
import { BookingSearchForm } from './BookingSearchForm';
import { ResultsGrid } from './ResultsGrid';
import LoginRegisterModal from './LoginRegisterModal';
import {
  MultiPlayerPricingModal,
  type MultiPlayerRatePick,
} from './MultiPlayerPricingModal';
import { ReservationConfirmationModal } from './ReservationConfirmationModal';
import { MemberSearch } from './MemberSearch';
import { reservationsService, ReservationsApiError } from '../../services/reservations';
import { productsService } from '../../services/products';
import { golfClubsService } from '../../services/golf-clubs';
import { taxCalculationService } from '../../services/tax-calculation';
import { customerAuthService, type BookingCustomer } from '../../services/customer-auth.service';
import {
  partitionCouponValidationForCheckoutUi,
  couponService,
  type Coupon,
  type CouponValidationResult,
} from '../../services/coupon-service';
import { passesService } from '../../services/passes.js';
import { bookingMemoService, type BookingMemo } from '../../services/booking-memo';
import { brandSettingsService } from '../../services/brand-settings';
import { getApiBaseUrl, apiClient } from '../../services/api';
import { memberDirectoryService } from '../../services/memberDirectory';
import { isBookingOAuthReturn, oauthReturnClubAuthFieldsFromSearch, parseBookingOAuthState } from '../../utils/booking-oauth-state';
import {
  BOOKING_SLUG_PATTERN,
  decodeClubRouteKey,
  parseBookingPathClubSlug,
  publicBookingApiParamsFromClubRouteKey,
  stripOAuthReturnSearchParams,
} from '../../utils/club-route-key';
import GolferDashboard from '../GolferDashboard/GolferDashboard';
import BookingSubNav from '../BookingShell/BookingSubNav';
import BookingAccountMenu from '../BookingShell/BookingAccountMenu';
import { fetchShopCatalog } from '../../services/shop';
import { BookingsIcon } from '../Icons/BookingsIcon';
import { PassesIcon } from '../Icons/PassesIcon';
import { CouponTagIcon } from '../Icons/CouponTagIcon';
import { customerCanViewTeeSheetPlayerNames } from '@boubou/types';
import { buildReservationTeeTimeId } from '../../utils/reservation-tee-time-id';
import OnlineBookingPaymentStep from './OnlineBookingPaymentStep';
import {
  buildPaymentSlotsFromPlayerSlots,
  createCheckoutIntent,
  fetchPaymentRequirements,
  onlinePaymentRequirementNeedsCheckout,
  type CheckoutIntentResponse,
} from '../../services/payments';
import type { OnlinePaymentRequirement } from '@boubou/types';

/**
 * Map `?clubId=` / `?clubNumber=` URL value to login/OAuth fields.
 * Numeric-only values are club numbers; anything else is treated as an opaque club id (CUID, UUID, etc.).
 * The old `length > 10` heuristic dropped valid short ids and broke production login.
 */
/**
 * Default: card grid (`TeeTimeCard`). Use `?teeLayout=list` (or `bookingLayout` / `resultsLayout`)
 * for Make-style rows (`TeeTimeListRow`). `?teeLayout=cards` is accepted but redundant.
 */
function bookingGridPresentationFromLocation(): 'cards' | 'list' {
  if (typeof window === 'undefined') return 'cards';
  const q = new URLSearchParams(window.location.search);
  for (const key of ['teeLayout', 'bookingLayout', 'resultsLayout'] as const) {
    const raw = q.get(key)?.trim().toLowerCase();
    if (raw === 'list' || raw === 'rows') return 'list';
    if (raw === 'cards') return 'cards';
  }
  return 'cards';
}

function readInitialShowLoginModal(): boolean {
  if (typeof window === 'undefined') return false;
  return isBookingOAuthReturn(window.location.search);
}

function clubAuthFieldsFromBookingUrl(
  clubRouteKey: string | null | undefined,
  clubNameFromUrl?: string | null,
): { clubId?: string; clubNumber?: number | string; clubName?: string; bookingSlug?: string } {
  const d = decodeClubRouteKey(clubRouteKey);
  const name = clubNameFromUrl?.trim() || undefined;
  return {
    ...(d.bookingSlug ? { bookingSlug: d.bookingSlug } : {}),
    ...(d.clubId ? { clubId: d.clubId } : {}),
    ...(d.clubNumber ? { clubNumber: d.clubNumber } : {}),
    ...(name ? { clubName: name } : {}),
  };
}

/** Prefer course/configured guest type when it has a rate on this tee time; else lowest green fee. */
function resolveGuestCustomerTypeIdFromPricingOptions(
  opts: Array<{ customerTypeId: string; price: number }>,
  preferredId?: string | null,
): string | undefined {
  if (!opts.length) return undefined;
  if (preferredId) {
    const match = opts.find((o) => o.customerTypeId === preferredId);
    if (match) return match.customerTypeId;
  }
  const sorted = [...opts].sort((a, b) => a.price - b.price);
  return sorted[0]?.customerTypeId;
}

// Define interfaces inline to bypass module resolution issues
// Updated: 2025-01-07 - Fixed date handling issues
interface TeeTime {
  id: string;
  courseName: string;
  courseId: string;
  dateTime: Date;
  originalDate?: string; // Original date string from backend (YYYY-MM-DD) - made optional
  /** Canonical id for POST /reservations (`YYYY-MM-DDTHH:mm` or with `|laneKey`). */
  reservationTeeTimeId?: string;
  /** Concurrent-slot key (F9/B9, shotgun hole, or "default") used to build the id above. */
  laneKey?: string;
  price: number;
  capacity: number;
  bookedCount: number;
  availableSpots: number;
  courseColor?: string; // Optional course color for the dot
  customerTypeId?: string; // Add customer type ID for pricing
  holeSet?: string; // Hole set (9 or 18) for the tee time
  pricingOptions?: Array<{
    customerTypeId: string;
    customerTypeName: string;
    price: number;
    color: string;
  }>; // All available pricing options for this tee time
  memberPlayers?: Array<{
    spotNumber: number;
    firstName: string;
    lastName: string;
    customerType: string;
  }>; // Member player information for member users
  dynamicPricing?: {
    direction: 'lower' | 'higher';
    percentRounded: number;
    underOnePercent: boolean;
  };
  promotion?: {
    type: 'percentage' | 'fixed';
    value: number;
    discountGreenFee?: boolean;
    discountCart?: boolean;
  };
  /** From getTeeTimeAvailability — same request as pricing; overrides stale courses for default guest. */
  courseDefaultGuestTypeId?: string;
  coursePreloadHalfCartWithGreenFee?: boolean;
  mandatoryHalfCartPreload?: boolean;
  /** True when `lowestPrice` / options include `halfCartAddon` from pricing (authoritative for card copy). */
  priceIncludesHalfCartAddon?: boolean;
}

/**
 * Online booking must not offer tee times that are already in the past.
 * - For a calendar day before today: return none.
 * - For today: keep only slots strictly after "now" (local clock).
 * - For a future day: keep all (entire day is still ahead).
 */
function filterBookableTeeTimesForOnlineSearch(searchDate: Date, teeTimes: TeeTime[], now: Date = new Date()): TeeTime[] {
  const searchDayStart = new Date(searchDate.getFullYear(), searchDate.getMonth(), searchDate.getDate());
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (searchDayStart.getTime() < todayStart.getTime()) {
    return [];
  }
  if (searchDayStart.getTime() > todayStart.getTime()) {
    return teeTimes;
  }
  return teeTimes.filter((tt) => tt.dateTime.getTime() > now.getTime());
}

interface Product {
  id: string;
  name: string;
  price?: number;
  category: string;
}

/** Stable empty array for product merges — avoids new refs on every render. */
const EMPTY_PRODUCTS: Product[] = [];

/**
 * Match pass `includedProducts` to a catalog product by id or loose name.
 * Mirrors API `reservationProductMatchesPassInclusion` so ticket preview matches
 * what gets discounted when product IDs differ across templates/clubs.
 */
function passCatalogProductMatchesInclusion(
  includedProducts: Array<{ productId: string; productName?: string | null }> | undefined | null,
  catalogProduct: { id: string; name?: string | null }
): boolean {
  if (!includedProducts?.length || !catalogProduct?.id) return false;
  if (includedProducts.some((p) => p.productId === catalogProduct.id)) return true;
  const pname = (catalogProduct.name || '').toLowerCase().trim();
  if (!pname) return false;
  for (const inc of includedProducts) {
    const iname = (inc.productName || '').toLowerCase().trim();
    if (!iname) continue;
    if (pname === iname || pname.includes(iname) || iname.includes(pname)) return true;
  }
  return false;
}

type PassIncludedProductRow = {
  productId: string;
  productName?: string | null;
  unitPrice?: number | null;
};

/**
 * Monetary amount for pass discount / pass-covered lines: use catalog price when > 0,
 * otherwise `includedProducts[].unitPrice` (public lists often omit or zero prices for members).
 */
function passDiscountUnitPrice(
  includedProducts: PassIncludedProductRow[] | undefined | null,
  catalogProduct: { id: string; name?: string | null; price?: number | null | undefined }
): number {
  const catalogRaw = catalogProduct.price;
  const fromCatalog = catalogRaw != null ? Number(catalogRaw) : NaN;
  if (Number.isFinite(fromCatalog) && fromCatalog > 0) return fromCatalog;
  if (!includedProducts?.length) return 0;

  const byId = includedProducts.find((p) => p.productId === catalogProduct.id);
  if (byId?.unitPrice != null) {
    const u = Number(byId.unitPrice);
    if (Number.isFinite(u) && u > 0) return u;
  }

  const pname = (catalogProduct.name || '').toLowerCase().trim();
  if (pname) {
    for (const inc of includedProducts) {
      if (inc.unitPrice == null) continue;
      const u = Number(inc.unitPrice);
      if (!Number.isFinite(u) || u <= 0) continue;
      const iname = (inc.productName || '').toLowerCase().trim();
      if (!iname) continue;
      if (pname === iname || pname.includes(iname) || iname.includes(pname)) return u;
    }
  }
  return 0;
}

function resolveGolfRoundProductForCheckout(allProducts: Product[], holeCount: number): Product | undefined {
  return allProducts.find(p => {
    const isGolfRound = p.category === 'golf-round';
    const nameMatches =
      p.name === `${holeCount} Hole - Green Fee` ||
      p.name === `ROUND OF GOLF - ${holeCount} HOLES` ||
      (p.name.toLowerCase().includes(`${holeCount} hole`) && p.name.toLowerCase().includes('green fee')) ||
      (p.name.toLowerCase().includes(`${holeCount} hole`) && p.name.toLowerCase().includes('golf'));
    return isGolfRound && nameMatches;
  });
}

function resolveCartProductForCheckout(allProducts: Product[], holeCount: number): Product | undefined {
  const cartProducts = allProducts.filter(p => p.category === 'cart-rental');
  const productsForHoles = cartProducts.filter(p => {
    const name = p.name.toUpperCase();
    const is18Hole = name.includes('18 HOLES') || name.includes('18-HOLE');
    const is9Hole = name.includes('9 HOLES') || name.includes('9-HOLE');
    if (holeCount === 18) return is18Hole;
    if (holeCount === 9) return is9Hole;
    return false;
  });
  const fallbackOrder = ['HALF', 'FULL', '1-PERSON'];
  for (const type of fallbackOrder) {
    const cartProduct = productsForHoles.find(p => {
      const productType = p.name.toUpperCase().includes('HALF')
        ? 'HALF'
        : p.name.toUpperCase().includes('FULL')
          ? 'FULL'
          : p.name.toUpperCase().includes('1 PERSON')
            ? '1-PERSON'
            : 'UNKNOWN';
      return productType === type;
    });
    if (cartProduct) return cartProduct;
  }
  return undefined;
}

/** Customer type id from member-directory search row (for per-player green fee). */
function getMemberDirectoryCustomerTypeId(member: any): string | undefined {
  if (!member) return undefined;
  const raw = member.customerTypeId;
  if (raw && typeof raw === 'object' && typeof raw.id === 'string') return raw.id;
  if (typeof raw === 'string') return raw;
  return undefined;
}

/**
 * Green-fee customer type for a partner linked from the member directory.
 * Directory CRM types are often guest buckets; online member booking should bill the tee time's **member** rate.
 * - Member booker: same rate as the booker (`selectedTeeTime.customerTypeId`, then first option).
 * - Guest booker: prefer a pricing option whose name looks like a member rate, else first option, else directory id if it matches an option.
 */
function getLinkedMemberGreenFeeCustomerTypeId(
  selectedTeeTime: Pick<TeeTime, 'customerTypeId' | 'pricingOptions'> | null | undefined,
  directoryMember: any,
  bookerIsMember: boolean
): string | undefined {
  const opts = selectedTeeTime?.pricingOptions;
  const directoryTid = getMemberDirectoryCustomerTypeId(directoryMember);
  const directoryTidValid = !!(directoryTid && opts?.some((o) => o.customerTypeId === directoryTid));

  if (bookerIsMember) {
    const memberRateId = selectedTeeTime?.customerTypeId || opts?.[0]?.customerTypeId;
    if (memberRateId) return memberRateId;
    return directoryTidValid ? directoryTid : undefined;
  }

  const publicMemberRateId =
    opts?.find((o) => /\bmember/i.test(o.customerTypeName || ''))?.customerTypeId ||
    opts?.[0]?.customerTypeId;
  if (publicMemberRateId) return publicMemberRateId;
  return directoryTidValid ? directoryTid : undefined;
}

function resolveGreenFeePriceFromOptions(
  pricingOptions: TeeTime['pricingOptions'] | undefined,
  customerTypeId: string | undefined,
  fallback: number
): number {
  if (!pricingOptions?.length || !customerTypeId) return fallback;
  const match = pricingOptions.find((o) => o.customerTypeId === customerTypeId);
  return match != null ? Number(match.price) || 0 : fallback;
}

/**
 * Pass-aware amounts for booking checkout: matches summary line items (one covered green fee / one covered cart when the pass includes those products).
 * When `perPlayerGreenFees` matches `searchPlayers`, green fee subtotal is the sum of each slot’s rate (booker may be waived by pass).
 */
function getPassAwareCheckoutPricing(
  selectedTeeTime: { holeSet?: string; price?: number } | null,
  searchPlayers: number,
  cartAssignments: number[],
  extras: any[] | undefined,
  extrasAssignments: Record<string, number[]>,
  allProducts: Product[],
  appliedPass: any | null,
  perPlayerGreenFees?: number[] | null
) {
  if (!selectedTeeTime) return null;

  const is18Hole = selectedTeeTime.holeSet === '18-hole' || selectedTeeTime.holeSet === '18';
  const holeCount = is18Hole ? 18 : 9;
  const golfRoundProduct = resolveGolfRoundProductForCheckout(allProducts, holeCount);
  const includedForPass: PassIncludedProductRow[] = appliedPass?.passProduct?.includedProducts || [];
  const hasInc = Array.isArray(includedForPass) && includedForPass.length > 0;

  const coversGreenFee = !!(
    appliedPass &&
    golfRoundProduct &&
    hasInc &&
    passCatalogProductMatchesInclusion(includedForPass, golfRoundProduct)
  );
  const coveredGreenPlayers = coversGreenFee ? 1 : 0;
  const fullPricePlayers = Math.max(0, searchPlayers - coveredGreenPlayers);

  const cartProduct =
    cartAssignments.length > 0 ? resolveCartProductForCheckout(allProducts, holeCount) : undefined;
  const coversCart = !!(
    appliedPass &&
    cartProduct &&
    hasInc &&
    passCatalogProductMatchesInclusion(includedForPass, cartProduct)
  );
  const coveredCartUnits = coversCart && cartAssignments.length > 0 ? 1 : 0;
  const chargedCarts = Math.max(0, cartAssignments.length - coveredCartUnits);

  const cartCatalogUnit = cartProduct ? Number(cartProduct.price) || 0 : 0;
  const greenUnit = selectedTeeTime.price || 0;

  const usePerPlayer =
    Array.isArray(perPlayerGreenFees) && perPlayerGreenFees.length === searchPlayers;

  let greenSubtotal = 0;
  if (usePerPlayer) {
    for (let i = 0; i < searchPlayers; i++) {
      if (coversGreenFee && i === 0) continue;
      greenSubtotal += Number(perPlayerGreenFees[i]) || 0;
    }
  } else {
    greenSubtotal = greenUnit * fullPricePlayers;
  }

  const extrasTotal = Array.isArray(extras)
    ? extras.reduce((total, extra) => {
        const assignments = extrasAssignments[extra.id] || [];
        return total + ((extra.price || 0) * assignments.length);
      }, 0)
    : 0;

  const fallbackSubtotalBeforeCoupon =
    greenSubtotal + chargedCarts * cartCatalogUnit + extrasTotal;

  return {
    golfRoundProduct,
    cartProduct,
    holeCount,
    fullPricePlayers,
    coveredGreenPlayers,
    chargedCarts,
    coversGreenFee,
    coversCart,
    includedForPass,
    cartCatalogUnit,
    greenUnit,
    fallbackSubtotalBeforeCoupon,
    extrasTotal,
    perPlayerGreenFees: usePerPlayer ? perPlayerGreenFees : null
  };
}

/** Two-letter initials for checkout player columns (booker = player 1; added members use their names). */
function initialsFromParts(first?: string | null, last?: string | null): string {
  const f = (first ?? '').trim();
  const l = (last ?? '').trim();
  const a = f ? f.charAt(0).toUpperCase() : '';
  const b = l ? l.charAt(0).toUpperCase() : '';
  if (a && b) return `${a}${b}`;
  if (f.length >= 2) return f.slice(0, 2).toUpperCase();
  if (a) return a;
  if (b) return b;
  return '';
}

function bookingCheckoutPlayerInitials(
  playerNumber: number,
  authenticatedCustomer: BookingCustomer | null | undefined,
  selectedMembers: Array<{ playerNumber: number; member: { firstName?: string; lastName?: string } }>
): string {
  if (playerNumber === 1) {
    if (authenticatedCustomer) {
      const s = initialsFromParts(authenticatedCustomer.firstName, authenticatedCustomer.lastName);
      if (s) return s;
    }
    return String(playerNumber);
  }
  const sm = selectedMembers.find((m) => m.playerNumber === playerNumber);
  if (sm?.member) {
    const s = initialsFromParts(sm.member.firstName, sm.member.lastName);
    if (s) return s;
  }
  return String(playerNumber);
}

function bookingCheckoutPlayerFullLabel(
  playerNumber: number,
  authenticatedCustomer: BookingCustomer | null | undefined,
  selectedMembers: Array<{ playerNumber: number; member: { firstName?: string; lastName?: string } }>
): string {
  if (playerNumber === 1) {
    if (authenticatedCustomer) {
      const n = `${authenticatedCustomer.firstName ?? ''} ${authenticatedCustomer.lastName ?? ''}`.trim();
      if (n) return n;
    }
    return `Player ${playerNumber}`;
  }
  const sm = selectedMembers.find((m) => m.playerNumber === playerNumber);
  if (sm?.member) {
    const n = `${sm.member.firstName ?? ''} ${sm.member.lastName ?? ''}`.trim();
    if (n) return n;
  }
  return `Player ${playerNumber}`;
}

/** Cart / extras player assignment toggle — selected + unselected use brand border radius. */
function CheckoutPlayerAssignmentToggle({
  size = 25.6,
  isAssigned,
  brandColors,
  borderColor,
  borderWidth = 1,
  checkIconSize,
}: {
  size?: number;
  isAssigned: boolean;
  brandColors: BookingBrandColors;
  borderColor: string;
  borderWidth?: number;
  checkIconSize?: number;
}) {
  const checkSize = checkIconSize ?? Math.round(size * 0.55);

  return (
    <div
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `${borderWidth}px solid ${isAssigned ? brandColors.buttonColor : borderColor}`,
        borderRadius: `var(--brand-border-radius, ${brandColors.borderRadius})`,
        backgroundColor: isAssigned ? brandColors.buttonColor : '#ffffff',
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
      aria-hidden
    >
      {isAssigned ? (
        <svg
          width={checkSize}
          height={checkSize}
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d="M11.6667 3.5L5.25004 9.91667L2.33337 7"
            stroke={brandColors.primaryTextColor}
            strokeWidth={ICON_STROKE.standard}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </div>
  );
}

// Use the optimized reservations service
const optimizedReservationsService = reservationsService;

const OnlineBookingEngine: React.FC = () => {
  const location = useLocation();
  const tebox = useBookingTebox();
  /** Very light row separator — TEBOX border.light at low alpha (replaces heavy item borders). */
  const checkoutItemDividerStyle = {
    borderBottom: `1px solid color-mix(in srgb, ${teboxDesignSystem.colors.border.light} 30%, transparent)`,
  } as React.CSSProperties;
  const { } = useAppState();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  /** Checkout section — white surface + border (matches section cards). */
  const checkoutHighlightPanelClass = `bg-white rounded-[6px] border border-tebox-border ${isMobile ? 'p-3' : 'p-4'} ${isMobile ? 'space-y-2' : 'space-y-3'}`;
  /** Passes / Coupons panels — subtle grey, lighter than solid `background.secondary`. */
  const checkoutAuxiliarySectionSurfaceStyle = {
    backgroundColor: `color-mix(in srgb, ${tebox.colors.bgSecondary} 28%, ${teboxDesignSystem.colors.background.primary})`,
  } as React.CSSProperties;
  const [searchDate, setSearchDate] = useState<Date>(() => {
    // Ensure we start with today's date in local timezone
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    return today;
  });
  const [searchPlayers, setSearchPlayers] = useState<number>(2);
  const [searchTimeOfDay, setSearchTimeOfDay] = useState<'AM' | 'PM' | 'Both'>('Both');
  const [searchHoles, setSearchHoles] = useState<'9' | '18' | 'Both'>('Both');
  const [searchCourses, setSearchCourses] = useState<string[]>([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedTeeTime, setSelectedTeeTime] = useState<TeeTime | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'teesheet'>('grid');
  const [blockedTeeTimes, setBlockedTeeTimes] = useState<Set<string>>(new Set());
  const [showDashboard, setShowDashboard] = useState(false);
  
  // Login/Register modal state
  const [showLoginModal, setShowLoginModal] = useState(readInitialShowLoginModal);
  const [authenticatedCustomer, setAuthenticatedCustomer] = useState<BookingCustomer | null>(null);

  // Coupon state
  const [promoCode, setPromoCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [availableCoupons, setAvailableCoupons] = useState<Coupon[]>([]);
  const [showCouponDropdown, setShowCouponDropdown] = useState(false);
  const [couponValidationResult, setCouponValidationResult] = useState<CouponValidationResult | null>(null);
  /** Neutral copy when a code is valid globally but doesn’t apply to this booking context. */
  const [couponApplicabilityHint, setCouponApplicabilityHint] = useState<string | null>(null);
  
  // Pass selection state
  const [availablePasses, setAvailablePasses] = useState<any[]>([]);
  const [selectedPass, setSelectedPass] = useState<any>(null);
  const [appliedPass, setAppliedPass] = useState<any>(null);
  const [passDiscount, setPassDiscount] = useState<number>(0);
  const [showPassDropdown, setShowPassDropdown] = useState(false);
  /** Set to tee time id when user clears pass — disables auto-prefill until tee time changes. */
  const passPrefillOptOutTeeIdRef = useRef<string | null>(null);
  
  // Brand settings state for dynamic styling
  const [brandColors, setBrandColors] = useState(() => resolveBookingBrandColors(null));

  /** Matches multi-player pricing modal `role="note"` chrome (brand wash + border mix). */
  const reservationSummaryBrandChrome = useMemo(
    () =>
      bookingBrandInfoChromeStyles({
        primaryColor: brandColors.primaryColor,
        buttonColor: brandColors.buttonColor,
      }),
    [brandColors.primaryColor, brandColors.buttonColor],
  );

  /** Pass/coupon-adjusted amounts — mix primary toward black so light brand colours stay legible. */
  const reservationSummaryAdjustedPriceColor = useMemo(
    () =>
      `color-mix(in srgb, ${(brandColors.primaryColor || '#000000').trim()} 46%, black)`,
    [brandColors.primaryColor],
  );

  // Hamburger menu state — account menu lives in BookingAccountMenu

  /** Desktop: collapse search filters + “Available Times” heading/summary while scrolling results down; expand on scroll up or near top. */
  const [bookingTopChromeVisible, setBookingTopChromeVisible] = useState(true);
  const lastBookingScrollTopRef = useRef(0);
  const bookingResultsScrollElRef = useRef<HTMLDivElement | null>(null);

  const applyBookingScrollForTopChrome = useCallback((scrollTop: number) => {
    const prev = lastBookingScrollTopRef.current;
    const delta = scrollTop - prev;
    lastBookingScrollTopRef.current = scrollTop;
    if (scrollTop < 24) {
      setBookingTopChromeVisible(true);
      return;
    }
    if (delta > 8) setBookingTopChromeVisible(false);
    else if (delta < -8) setBookingTopChromeVisible(true);
  }, []);

  const onBookingResultsScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (isMobile) return;
      applyBookingScrollForTopChrome(e.currentTarget.scrollTop);
    },
    [isMobile, applyBookingScrollForTopChrome],
  );

  useLayoutEffect(() => {
    if (isMobile) return;
    lastBookingScrollTopRef.current = bookingResultsScrollElRef.current?.scrollTop ?? 0;
    setBookingTopChromeVisible(true);
  }, [isMobile]);

  // Booking memo state
  const [bookingMemo, setBookingMemo] = useState<BookingMemo | null>(null);

  // Prefixed route key: `s:` slug, `i:` club id, `n:` club number (legacy bare values still decoded).
  const clubIdOrNumber = useMemo(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(location.search);

      const code = params.get('code');
      const oauthStateRaw = params.get('state');
      if ((code || params.get('id_token')) && oauthStateRaw) {
        const parsed = parseBookingOAuthState(oauthStateRaw);
        const newUrl = new URL(window.location.href);
        stripOAuthReturnSearchParams(newUrl.searchParams);

        if (parsed?.bookingSlug?.trim()) {
          const slug = parsed.bookingSlug.trim().toLowerCase();
          if (BOOKING_SLUG_PATTERN.test(slug)) {
            newUrl.searchParams.set('bookingSlug', slug);
            window.history.replaceState({}, '', newUrl.toString());
            console.log('🎨 Club booking slug from OAuth state:', slug);
            return `s:${slug}`;
          }
        }
        if (parsed?.clubNumber?.trim()) {
          const n = parsed.clubNumber.trim();
          newUrl.searchParams.set('clubNumber', n);
          window.history.replaceState({}, '', newUrl.toString());
          console.log('🎨 Club number from OAuth state:', n);
          return `n:${n}`;
        }
        if (parsed?.clubId?.trim()) {
          const id = parsed.clubId.trim();
          newUrl.searchParams.set('clubId', id);
          window.history.replaceState({}, '', newUrl.toString());
          console.log('🎨 Club id from OAuth state:', id);
          return /^\d+$/.test(id) ? `n:${id}` : `i:${id}`;
        }
      }

      const slugQ = params.get('bookingSlug')?.trim().toLowerCase();
      if (slugQ && BOOKING_SLUG_PATTERN.test(slugQ)) {
        console.log('🎨 bookingSlug from query:', slugQ);
        return `s:${slugQ}`;
      }

      const clubIdParam = params.get('clubId')?.trim();
      if (clubIdParam) {
        const key = /^\d+$/.test(clubIdParam) ? `n:${clubIdParam}` : `i:${clubIdParam}`;
        console.log('🎨 clubId from query → route key:', key);
        return key;
      }

      const clubNumParam = params.get('clubNumber')?.trim();
      if (clubNumParam) {
        console.log('🎨 clubNumber from query:', clubNumParam);
        return `n:${clubNumParam}`;
      }

      const storedSlug = sessionStorage.getItem('oauth_booking_slug')?.trim().toLowerCase();
      if (storedSlug && BOOKING_SLUG_PATTERN.test(storedSlug)) {
        console.log('🎨 booking slug from sessionStorage:', storedSlug);
        return `s:${storedSlug}`;
      }

      const storedClubId = sessionStorage.getItem('oauth_club_id')?.trim();
      if (storedClubId) {
        if (window.location.pathname.includes('/oauth/callback')) {
          const newUrl = new URL(window.location.href);
          stripOAuthReturnSearchParams(newUrl.searchParams);
          newUrl.searchParams.set('clubId', storedClubId);
          window.history.replaceState({}, '', newUrl.toString());
        }
        const key = /^\d+$/.test(storedClubId) ? `n:${storedClubId}` : `i:${storedClubId}`;
        console.log('🎨 club id from sessionStorage → route key:', key);
        return key;
      }

      const storedClubNumber = sessionStorage.getItem('oauth_club_number')?.trim();
      if (storedClubNumber) {
        if (window.location.pathname.includes('/oauth/callback')) {
          const newUrl = new URL(window.location.href);
          stripOAuthReturnSearchParams(newUrl.searchParams);
          newUrl.searchParams.set('clubNumber', storedClubNumber);
          window.history.replaceState({}, '', newUrl.toString());
        }
        console.log('🎨 club number from sessionStorage:', storedClubNumber);
        return `n:${storedClubNumber}`;
      }

      const pathSlug = parseBookingPathClubSlug(location.pathname);
      if (pathSlug) {
        console.log('🎨 booking slug from path:', pathSlug);
        return `s:${pathSlug}`;
      }

      if (import.meta.env.DEV || window.location.hostname === 'localhost') {
        console.log('🔧 Development mode: Using default clubNumber=1');
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('clubNumber', '1');
        window.history.replaceState({}, '', newUrl.toString());
        return 'n:1';
      }

      console.log('🎨 No club route key from URL/session/path');
      return null;
    }
    return null;
  }, [location.pathname, location.search]);

  /** After club inject `replaceState`, so `window.location` matches what the user sees. */
  const gridPresentation = useMemo(
    () => bookingGridPresentationFromLocation(),
    [clubIdOrNumber, location.pathname, location.search],
  );

  const clubNameFromUrl = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const oauthStateRaw = params.get('state');
    if ((code || params.get('id_token')) && oauthStateRaw) {
      const parsed = parseBookingOAuthState(oauthStateRaw);
      if (parsed?.clubName?.trim()) return parsed.clubName.trim();
    }
    return params.get('clubName')?.trim() || undefined;
  }, [location.pathname, location.search]);

  const isOAuthReturn = useMemo(
    () => isBookingOAuthReturn(location.search),
    [location.search],
  );

  const oauthReturnClubAuth = useMemo(
    () => oauthReturnClubAuthFieldsFromSearch(location.search),
    [location.search],
  );

  // Monitor showDashboard state changes
  useEffect(() => {
    console.log('🔍 showDashboard state changed:', showDashboard);
  }, [showDashboard]);

  // Helpers for pass validation (date/day/time) to mirror backend rules and
  // keep OnlineBooking consistent with the admin ReservationModal.
  const timeToMinutes = (time: string) => {
    const [h, m] = time.split(':').map((v) => parseInt(v, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const isPassApplicableToSelectedTeeTime = (pass: any): boolean => {
    try {
      if (!selectedTeeTime) return true;

      const teeDate = selectedTeeTime.dateTime;
      const teeTimeStr = selectedTeeTime.dateTime.toTimeString().slice(0, 5); // HH:MM

      // Subscription-level validity window
      if (pass.validFrom && new Date(pass.validFrom) > teeDate) {
        return false;
      }
      if (pass.validTo && new Date(pass.validTo) < teeDate) {
        return false;
      }

      const restrictions = pass.passProduct?.restrictions || [];

      // Date range restriction
      const dateRestriction = restrictions.find((r: any) => r.restrictionType === 'date_range');
      if (dateRestriction?.restrictionValue) {
        const parsed = JSON.parse(dateRestriction.restrictionValue as string);
        const validFrom = parsed.validFrom ? new Date(parsed.validFrom) : null;
        const validTo = parsed.validTo ? new Date(parsed.validTo) : null;
        if (validFrom && teeDate < validFrom) return false;
        if (validTo && teeDate > validTo) return false;
      }

      // Day-of-week restriction
      const dayRestriction = restrictions.find((r: any) => r.restrictionType === 'day_of_week');
      if (dayRestriction?.restrictionValue) {
        const parsed = JSON.parse(dayRestriction.restrictionValue as string);
        const allowedDays: string[] = Array.isArray(parsed) ? parsed : parsed.days || [];
        if (allowedDays.length > 0) {
          const dayOfWeek = teeDate.toLocaleDateString('en-US', { weekday: 'long' });
          if (!allowedDays.includes(dayOfWeek)) {
            return false;
          }
        }
      }

      // Time window restriction
      const timeRestriction = restrictions.find((r: any) => r.restrictionType === 'time_range');
      if (timeRestriction?.restrictionValue) {
        const parsed = JSON.parse(timeRestriction.restrictionValue as string);
        const startTime: string | undefined = parsed.startTime || parsed.start;
        const endTime: string | undefined = parsed.endTime || parsed.end;
        if (startTime && endTime) {
          const usage = timeToMinutes(teeTimeStr);
          const start = timeToMinutes(startTime);
          const end = timeToMinutes(endTime);
          if (usage !== null && start !== null && end !== null) {
            if (usage < start || usage > end) {
              return false;
            }
          }
        }
      }

      return true;
    } catch (error) {
      console.error('❌ Error validating pass against tee time in OnlineBookingEngine:', error);
      // Fail open to avoid blocking passes due to bad restriction data
      return true;
    }
  };

  // New tee time → allow pass auto-prefill again
  useEffect(() => {
    passPrefillOptOutTeeIdRef.current = null;
  }, [selectedTeeTime?.id]);

  // Apply cached brand settings immediately (before first paint) to prevent flash of default styling
  useLayoutEffect(() => {
    if (clubIdOrNumber) {
      const cached = brandSettingsService.getCachedBrandSettings(clubIdOrNumber);
      if (cached) {
        brandSettingsService.applyBrandSettingsSync(cached);
        setBrandColors(resolveBookingBrandColors(cached));
      }
    }
  }, [clubIdOrNumber]);

  // Fetch and apply brand settings when clubIdOrNumber is available
  const { data: brandSettingsData, isFetching: isBrandSettingsFetching } = useQuery({
    queryKey: ['brandSettings', clubIdOrNumber],
    queryFn: async () => {
      if (!clubIdOrNumber) return null;
      console.log('🎨 Fetching brand settings for club:', clubIdOrNumber);
      const settings = await brandSettingsService.getByClubId(clubIdOrNumber);
      console.log('🎨 Brand settings loaded:', settings);
      return settings;
    },
    enabled: !!clubIdOrNumber,
    initialData: clubIdOrNumber ? brandSettingsService.getCachedBrandSettings(clubIdOrNumber) ?? undefined : undefined,
    staleTime: 0, // Always consider stale - refetch to get latest settings
    refetchInterval: 3000, // Refetch every 3 seconds to catch admin updates
    refetchOnWindowFocus: true, // Refetch when user switches back to booking tab after saving in admin
  });

  // Refetch brand settings when page becomes visible (e.g. user returns from admin tab after saving)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && clubIdOrNumber) {
        queryClient.invalidateQueries({ queryKey: ['brandSettings', clubIdOrNumber] });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [queryClient, clubIdOrNumber]);

  const { data: shopCatalog } = useQuery({
    queryKey: ['shopCatalog', clubIdOrNumber],
    queryFn: () => fetchShopCatalog(clubIdOrNumber),
    enabled: !!clubIdOrNumber,
  });

  // Apply brand settings when they're loaded or updated
  useEffect(() => {
    if (brandSettingsData) {
      console.log('🎨 Brand settings data received:', {
        fontFamily: brandSettingsData.fontFamily,
        primaryColor: brandSettingsData.primaryColor,
        buttonColor: brandSettingsData.buttonColor,
        primaryTextColor: brandSettingsData.primaryTextColor,
        buttonTextColor: brandSettingsData.buttonTextColor,
        fullData: brandSettingsData
      });
      
      // Apply CSS variables immediately so tee-card hero typography updates before font load
      brandSettingsService.applyBrandSettingsSync(brandSettingsData);

      // Update React state immediately (for inline styles)
      const newBrandColors = resolveBookingBrandColors(brandSettingsData);
      
      console.log('🎨 Setting brand colors state:', newBrandColors);
      console.log('🎨 Current brandColors state before update:', brandColors);
      
      // Apply CSS variables first (async to wait for font loading)
      brandSettingsService.applyBrandSettings(brandSettingsData).then(() => {
        console.log('🎨 Brand settings applied successfully to DOM');
        console.log('🎨 CSS variables after application:', {
          '--brand-primary': getComputedStyle(document.documentElement).getPropertyValue('--brand-primary'),
          '--brand-font': getComputedStyle(document.documentElement).getPropertyValue('--brand-font'),
          '--brand-primary-text': getComputedStyle(document.documentElement).getPropertyValue('--brand-primary-text')
        });
        // Update state after CSS variables are set to ensure consistency
        setBrandColors(newBrandColors);
        
        // Force a small delay to ensure font is fully loaded before rendering
        setTimeout(() => {
          // Trigger a re-render by updating state again (this ensures all components get the new font)
          setBrandColors(prev => {
            console.log('🎨 Updating fontFamily in state:', newBrandColors.fontFamily);
            return { ...prev, fontFamily: newBrandColors.fontFamily };
          });
        }, 100);
      }).catch((error) => {
        console.error('❌ Error applying brand settings:', error);
        // Still set state even if CSS application fails
        setBrandColors(newBrandColors);
      });
    } else {
      console.log('🎨 No brand settings data available yet');
    }
  }, [brandSettingsData]);

  // Check for existing session on component mount
  useEffect(() => {
    console.log('🔍 OnlineBookingEngine: useEffect triggered - checking for existing session...');
    const checkExistingSession = async () => {
      console.log('🔍 OnlineBookingEngine: Checking for existing session...');
      const existingUser = await customerAuthService.getCurrentUser();
      if (existingUser) {
        console.log('🔍 OnlineBookingEngine: Found existing session:', existingUser);
        setAuthenticatedCustomer(existingUser);
      } else {
        console.log('🔍 OnlineBookingEngine: No existing session found');
        setAuthenticatedCustomer(null);
      }
    };
    
    checkExistingSession();
  }, []);

  /** Keep shared `apiClient` aligned with customer session so coupon/tax calls send `Authorization`. */
  useEffect(() => {
    const token = customerAuthService.getAccessToken();
    if (token) {
      apiClient.setAuthToken(token);
    } else {
      apiClient.clearAuthToken();
    }
  }, [authenticatedCustomer]);

  /** Drop stale sessions from another club when the booking portal URL changes. */
  useEffect(() => {
    const portalClubId = brandSettingsData?.clubId;
    const sessionClubId = authenticatedCustomer?.clubId;
    if (!portalClubId || !sessionClubId || portalClubId === sessionClubId) {
      return;
    }
    console.warn(
      'Booking portal club does not match customer session — clearing auth',
      { portalClubId, sessionClubId },
    );
    void customerAuthService.logout().then(() => {
      setAuthenticatedCustomer(null);
      apiClient.clearAuthToken();
    });
  }, [brandSettingsData?.clubId, authenticatedCustomer?.clubId]);

  // TODO: Re-enable periodic session validation when backend is ready
  /*
  // Periodically check session validity
  useEffect(() => {
    const interval = setInterval(async () => {
      if (authenticatedCustomer) {
        const currentUser = await customerAuthService.getCurrentUser();
        if (!currentUser) {
          console.log('🔍 Session expired, clearing authentication state');
          setAuthenticatedCustomer(null);
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [authenticatedCustomer]);
  */
  
  // Pricing modal state
  const [showPerPlayerRatesModal, setShowPerPlayerRatesModal] = useState(false);
  const [perPlayerRatesModalNonce, setPerPlayerRatesModalNonce] = useState(0);
  const [selectedTeeTimeForPricing, setSelectedTeeTimeForPricing] = useState<TeeTime | null>(null);

  // Confirmation modal state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [reservationDetails, setReservationDetails] = useState<{
    teeTime: string;
    course: string;
    date: string;
    players: number;
    price: number;
    reservationId?: string;
    /** Set from the reservation response for the wallet-pass link on the confirmation modal. */
    mobilePassAccessToken?: string;
    successSubtitle?: string;
    staffMessageNote?: { text: string; variant: 'success' | 'warning' };
  } | null>(null);

  const [partnerInviteUrl, setPartnerInviteUrl] = useState<string | null>(null);
  const [partnerInviteLoading, setPartnerInviteLoading] = useState(false);
  const [partnerInviteError, setPartnerInviteError] = useState<string | null>(null);

  // State for player assignments (replaces quantities)
  const [cartAssignments, setCartAssignments] = useState<number[]>([]);
  const [extrasAssignments, setExtrasAssignments] = useState<Record<string, number[]>>({});
  
  // State for mobile dropdowns
  const [openCartDropdown, setOpenCartDropdown] = useState(false);
  const [openExtrasDropdowns, setOpenExtrasDropdowns] = useState<Record<string, boolean>>({});
  
  // State for reservation creation
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);

  // State for member search
  const [showMemberSearch, setShowMemberSearch] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<Array<{
    playerNumber: number;
    member: any;
  }>>([]);
  /** Player 2+ slots without a linked member: customer type id for green fee & reservation guest typing. */
  const [companionGuestCustomerTypeByPlayer, setCompanionGuestCustomerTypeByPlayer] = useState<
    Record<number, string>
  >({});
  
  // Customer message state
  const [customerMessage, setCustomerMessage] = useState('');

  // State for player search (elastic search in input fields)
  const [playerSearchQueries, setPlayerSearchQueries] = useState<Array<string>>([]);
  const [playerSearchResults, setPlayerSearchResults] = useState<Array<Array<any>>>([]);
  const [isPlayerSearching, setIsPlayerSearching] = useState<Array<boolean>>([]);
  const [activePlayerSearchIndex, setActivePlayerSearchIndex] = useState<number | null>(null);

  // Initialize player search arrays when searchPlayers changes
  useEffect(() => {
    const playerCount = Math.max(0, searchPlayers - 1); // Number of additional players
    setPlayerSearchQueries(Array(playerCount).fill(''));
    setPlayerSearchResults(Array(playerCount).fill([]));
    setIsPlayerSearching(Array(playerCount).fill(false));
  }, [searchPlayers]);

  // Close dropdowns when clicking outside (mobile only)
  useEffect(() => {
    if (!isMobile) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Close cart dropdown if clicking outside
      if (openCartDropdown && !target.closest('[data-cart-dropdown]')) {
        setOpenCartDropdown(false);
      }
      // Close extras dropdowns if clicking outside
      Object.keys(openExtrasDropdowns).forEach(extraId => {
        if (openExtrasDropdowns[extraId] && !target.closest(`[data-extra-dropdown="${extraId}"]`)) {
          setOpenExtrasDropdowns(prev => ({ ...prev, [extraId]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, openCartDropdown, openExtrasDropdowns]);

  // Player search function
  const searchPlayersForInput = useCallback(async (query: string, playerIndex: number) => {
    console.log('🔍 searchPlayers called with query:', query, 'for player:', playerIndex);
    if (!query.trim()) {
      console.log('🔍 Empty query, clearing results');
      setPlayerSearchResults(prev => {
        const newResults = [...prev];
        newResults[playerIndex] = [];
        return newResults;
      });
      return;
    }

    console.log('🔍 Starting player search for:', query);
    setIsPlayerSearching(prev => {
      const newSearching = [...prev];
      newSearching[playerIndex] = true;
      return newSearching;
    });
    
    try {
      // Use memberDirectoryService for proper authentication and club context
      const d = decodeClubRouteKey(clubIdOrNumber ?? undefined);
      const clubNumberForSearch =
        d.clubNumber && /^\d+$/.test(d.clubNumber) ? Number(d.clubNumber) : undefined;
      const searchResult = await memberDirectoryService.searchMembers(query, 10, clubNumberForSearch);
      const members = searchResult.members;
      
      console.log('🔍 Player search results:', members.length, 'members found');
      console.log('🔍 Member details:', members);
      setPlayerSearchResults(prev => {
        const newResults = [...prev];
        newResults[playerIndex] = members;
        return newResults;
      });
    } catch (error) {
      console.error('❌ Error searching players:', error);
      setPlayerSearchResults(prev => {
        const newResults = [...prev];
        newResults[playerIndex] = [];
        return newResults;
      });
    } finally {
      setIsPlayerSearching(prev => {
        const newSearching = [...prev];
        newSearching[playerIndex] = false;
        return newSearching;
      });
    }
  }, [clubIdOrNumber]);

  // Debounced player search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activePlayerSearchIndex !== null) {
        searchPlayersForInput(playerSearchQueries[activePlayerSearchIndex], activePlayerSearchIndex);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [playerSearchQueries, activePlayerSearchIndex, searchPlayersForInput]);

  // Handle player input change
  const handlePlayerInputChange = (playerIndex: number, value: string) => {
    setPlayerSearchQueries(prev => {
      const newQueries = [...prev];
      newQueries[playerIndex] = value;
      return newQueries;
    });
    setActivePlayerSearchIndex(playerIndex);
  };

  // Handle member selection from dropdown
  const handleMemberSelectFromDropdown = (playerIndex: number, member: any) => {
    console.log('🔍 Member selected from dropdown:', member, 'for player index:', playerIndex);
    const playerNumber = playerIndex + 2; // Player 2, 3, 4, etc.
    
    // Add to selected members
    setSelectedMembers(prev => {
      const filtered = prev.filter(sm => sm.playerNumber !== playerNumber);
      const newSelection = [...filtered, { playerNumber, member }];
      console.log('🔍 Updated selected members:', newSelection);
      return newSelection;
    });
    
    setCompanionGuestCustomerTypeByPlayer((prev) => {
      const next = { ...prev };
      delete next[playerNumber];
      return next;
    });
    
    // Clear search query and results
    setPlayerSearchQueries(prev => {
      const newQueries = [...prev];
      newQueries[playerIndex] = '';
      return newQueries;
    });
    setPlayerSearchResults(prev => {
      const newResults = [...prev];
      newResults[playerIndex] = [];
      return newResults;
    });
    setActivePlayerSearchIndex(null);
  };

  // Handle tee time booking - show pricing modal first
  const handleTeeTimeBook = (teeTime: TeeTime) => {
    // Validate tee time availability before allowing booking
    console.log('🔍 Validating tee time before booking:', {
      id: teeTime.id,
      availableSpots: teeTime.availableSpots,
      capacity: teeTime.capacity,
      bookedCount: teeTime.bookedCount,
      requestedPlayers: searchPlayers
    });

    // Check if tee time has enough available spots
    if (teeTime.availableSpots < searchPlayers) {
      const errorMessage = `Not enough available spots. Only ${teeTime.availableSpots} spots available, but ${searchPlayers} players requested.`;
      console.error('❌ Tee time booking blocked:', errorMessage);
      setReservationError(errorMessage);
      return;
    }

    // Check if tee time is blocked (availableSpots = 0 and bookedCount = 0 might indicate blocked)
    if (teeTime.availableSpots === 0 && teeTime.bookedCount === 0) {
      const errorMessage = 'This tee time is blocked and not available for booking.';
      console.error('❌ Tee time booking blocked:', errorMessage);
      setReservationError(errorMessage);
      return;
    }

    // Check if requested players exceed capacity
    if (searchPlayers > teeTime.capacity) {
      const errorMessage = `Too many players. Maximum capacity is ${teeTime.capacity} players, but ${searchPlayers} players requested.`;
      console.error('❌ Tee time booking blocked:', errorMessage);
      setReservationError(errorMessage);
      return;
    }

    console.log('✅ Tee time validation passed, proceeding with booking');
    setReservationError(null); // Clear any previous errors

    // Per-player rate picker — only after sign-in (rates depend on member/guest context).
    if (teeTime.pricingOptions && teeTime.pricingOptions.length > 0) {
      if (!authenticatedCustomer) {
        setSelectedTeeTime(teeTime);
        setShowLoginModal(true);
        return;
      }
      console.log('🔍 Opening per-player rates modal:', teeTime.pricingOptions);
      setPerPlayerRatesModalNonce((n) => n + 1);
      setSelectedTeeTimeForPricing(teeTime);
      setShowPerPlayerRatesModal(true);
    } else {
      // No pricing options, proceed directly
      console.log('🔍 No pricing options, proceeding directly');
      setSelectedTeeTime(teeTime);
      if (!authenticatedCustomer) {
        setShowLoginModal(true);
      } else {
        setShowCheckout(true);
      }
    }
  };

  // Handle successful authentication
  const handleAuthenticationSuccess = (customer: any) => {
    setAuthenticatedCustomer(customer);
    setShowLoginModal(false);
    if (!selectedTeeTime) return;

    const hasPricingOptions =
      selectedTeeTime.pricingOptions && selectedTeeTime.pricingOptions.length > 0;
    if (hasPricingOptions) {
      setPerPlayerRatesModalNonce((n) => n + 1);
      setSelectedTeeTimeForPricing(selectedTeeTime);
      setShowPerPlayerRatesModal(true);
      return;
    }

    setShowCheckout(true);
  };

  // Handle login modal close
  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    // Don't clear selectedTeeTime here - let the user go back to search if they want
  };

  // Handle member selection from search
  const handleMemberSelect = (member: any) => {
    // Find the next available player slot
    const usedPlayerNumbers = selectedMembers.map(sm => sm.playerNumber);
    const nextPlayerNumber = Array.from({ length: searchPlayers - 1 }, (_, i) => i + 2)
      .find(num => !usedPlayerNumbers.includes(num));
    
    if (nextPlayerNumber) {
      setSelectedMembers(prev => [...prev, {
        playerNumber: nextPlayerNumber,
        member: member
      }]);
    }
  };

  // Handle removing a selected member
  const handleRemoveMember = (playerNumber: number) => {
    setSelectedMembers(prev => prev.filter(sm => sm.playerNumber !== playerNumber));
  };

  // Handle confirmation modal close
  const handleConfirmationModalClose = () => {
    setShowConfirmationModal(false);
    setReservationDetails(null);
    setPartnerInviteUrl(null);
    setPartnerInviteError(null);
    setPartnerInviteLoading(false);
    // Go back to search after closing the confirmation modal
    setShowCheckout(false);
    setSelectedTeeTime(null);
    setCompanionGuestCustomerTypeByPlayer({});
    // Reset assignments when going back
    setCartAssignments([]);
    // Clear customer message when closing confirmation modal
    setCustomerMessage('');
    setExtrasAssignments({});
    // Clear messages
    setReservationError(null);

    // Refresh tee sheet after confirmation — avoids flicker while modal is open
    queryClient.invalidateQueries({
      queryKey: ['teeTimes', searchDate, searchPlayers, searchTimeOfDay, searchHoles, searchCourses],
    });
    queryClient.invalidateQueries({ queryKey: ['teeTimes'] });
    window.dispatchEvent(new CustomEvent('force-refresh-teesheet'));
  };

  const handleMintPartnerInvite = useCallback(async () => {
    const rid = reservationDetails?.reservationId;
    if (!rid || !authenticatedCustomer) {
      setPartnerInviteError('Sign in to create an invite link.');
      return;
    }
    const token = customerAuthService.getAccessToken();
    if (!token) {
      setPartnerInviteError('Sign in to create an invite link.');
      return;
    }
    setPartnerInviteLoading(true);
    setPartnerInviteError(null);
    try {
      apiClient.setAuthToken(token);
      const res = await apiClient.post<{ success: boolean; inviteUrl: string; expiresAt: string }>(
        `/customer-auth/reservations/${encodeURIComponent(rid)}/partner-invite`,
        {}
      );
      if (res.success && res.inviteUrl) {
        setPartnerInviteUrl(res.inviteUrl);
      } else {
        setPartnerInviteError('Could not create invite link.');
      }
    } catch (e: unknown) {
      setPartnerInviteError(e instanceof Error ? e.message : 'Could not create invite link.');
    } finally {
      setPartnerInviteLoading(false);
    }
  }, [reservationDetails?.reservationId, authenticatedCustomer]);

  const [reservationError, setReservationError] = useState<string | null>(null);
  const [checkoutPaymentRequirement, setCheckoutPaymentRequirement] =
    useState<OnlinePaymentRequirement | null>(null);
  const [checkoutIntentData, setCheckoutIntentData] = useState<CheckoutIntentResponse | null>(null);
  const [onlinePaymentIntentId, setOnlinePaymentIntentId] = useState<string | undefined>();
  const [onlineSetupIntentId, setOnlineSetupIntentId] = useState<string | undefined>();
  const [paymentStepError, setPaymentStepError] = useState<string | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [taxCalculation, setTaxCalculation] = useState<{
    subtotal: number;
    taxAmount: number;
    total: number;
    taxBreakdown: Array<{
      taxClassId: string;
      taxClassName: string;
      taxRate: number;
      taxableAmount: number;
      taxAmount: number;
    }>;
  } | null>(null);

  // Fetch available courses - REQUIRES clubId or clubNumber for multi-tenant support
  const { data: courses = [], error: coursesError, isLoading: coursesLoading } = useQuery({
    queryKey: ['courses', clubIdOrNumber],
    queryFn: async () => {
      console.log('🔍 Fetching courses from API with clubIdOrNumber:', clubIdOrNumber);
      if (!clubIdOrNumber) {
        console.error('❌ clubId or clubNumber is required to fetch courses');
        throw new Error('clubId or clubNumber is required. Please add ?clubId=... or ?clubNumber=... to the URL');
      }
      try {
        const result = await golfClubsService.getCoursesWithTemplates(clubIdOrNumber);
        console.log('✅ Courses fetched successfully:', result?.length || 0, 'courses');
        return result;
      } catch (error: any) {
        console.error('❌ Error fetching courses:', error);
        throw error;
      }
    },
    enabled: !!clubIdOrNumber,
    retry: 1,
    retryDelay: 1000,
    // Course flags (mandatory carts / preload half cart, default guest type) must not stay frozen after admin updates.
    staleTime: 60_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const bookingMemoCourseId = searchCourses[0] ?? courses[0]?.id;
  const bookingMemoDateStr = searchDate.toISOString().split('T')[0];

  useEffect(() => {
    if (!showCheckout || !clubIdOrNumber) return;
    void queryClient.refetchQueries({ queryKey: ['courses', clubIdOrNumber] });
  }, [showCheckout, clubIdOrNumber, queryClient]);

  // Fetch booking memo for selected date and course
  useEffect(() => {
    if (!bookingMemoCourseId) {
      setBookingMemo(null);
      return;
    }

    let cancelled = false;

    const fetchBookingMemo = async () => {
      console.log('📋 Fetching booking memo for courseId:', bookingMemoCourseId, 'date:', bookingMemoDateStr);

      try {
        const memos = await bookingMemoService.getMemosForDate(bookingMemoCourseId, bookingMemoDateStr);
        if (cancelled) return;
        console.log('📋 Booking memos received:', memos);
        setBookingMemo(memos.length > 0 ? memos[0] : null);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching booking memo:', error);
        setBookingMemo(null);
      }
    };

    void fetchBookingMemo();
    return () => {
      cancelled = true;
    };
  }, [bookingMemoCourseId, bookingMemoDateStr]);

  // Debug courses fetching
  React.useEffect(() => {
    if (coursesError) {
      console.error('❌ Failed to fetch courses:', coursesError);
      console.error('❌ Error details:', {
        message: coursesError.message,
        stack: coursesError.stack,
        name: coursesError.name
      });
    } else if (courses && courses.length > 0) {
      console.log('✅ Successfully fetched courses:', courses);
      console.log('✅ Number of courses:', courses.length);
    }
  }, [courses, coursesError]);

  // Fetch default guest customer type
  const { data: defaultGuestCustomerTypeId } = useQuery({
    queryKey: ['defaultGuestCustomerType'],
    queryFn: () => optimizedReservationsService.getDefaultGuestCustomerType(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Check if authenticated user is a member
  // Robust validation that handles multiple customer shapes from the API.
  const isMember = useCallback((customer: any) => {
    if (!customer) {
      return false;
    }

    let category: string | undefined;

    // Highest priority: explicit category on customerTypeId (this is what the main app uses)
    if (customer.customerTypeId && typeof customer.customerTypeId === 'object') {
      if (typeof customer.customerTypeId.category === 'string') {
        category = customer.customerTypeId.category.toLowerCase().trim();
      }
    }

    // Next: category on customerType object
    if (!category && customer.customerType && typeof customer.customerType === 'object') {
      if (typeof customer.customerType.category === 'string') {
        category = customer.customerType.category.toLowerCase().trim();
      }
    }

    // Next: infer from a descriptive name like "Weekday Members"
    if (!category && typeof customer.customerType === 'string') {
      const name = customer.customerType.toLowerCase();
      if (name.includes('member')) {
        category = 'member';
      } else if (name.includes('guest')) {
        category = 'guest';
      }
    }

    // Lowest priority: treat bare string 'member' | 'guest' as category
    if (!category && typeof customer.customerType === 'string') {
      const raw = customer.customerType.toLowerCase().trim();
      if (raw === 'member' || raw === 'guest') {
        category = raw;
      }
    }

    if (!category) {
      return false;
    }

    const isMemberCategory = category === 'member';
    return isMemberCategory;
  }, []);
  
  const isMemberResult = isMember(authenticatedCustomer);

  const teeSheetPlayerNamesVisible = useMemo(
    () =>
      !!authenticatedCustomer &&
      (customerCanViewTeeSheetPlayerNames(authenticatedCustomer) ||
        isMember(authenticatedCustomer)),
    [authenticatedCustomer, isMember],
  );

  // Helper: normalize customerTypeId to a bare ID string for downstream services (coupons, pricing, etc.)
  const getCustomerTypeIdString = (customer: any | null | undefined): string | undefined => {
    if (!customer) return undefined;
    const raw = customer.customerTypeId;
    if (raw && typeof raw === 'object' && typeof raw.id === 'string') {
      return raw.id;
    }
    if (typeof raw === 'string') {
      return raw;
    }
    return undefined;
  };

  // Fetch available tee times
  const { data: teeTimes = [], isLoading, error, refetch } = useQuery({
    queryKey: ['teeTimes', searchDate, searchPlayers, searchTimeOfDay, searchHoles, searchCourses, getCustomerTypeIdString(authenticatedCustomer), isMemberResult, viewMode],
    queryFn: async () => {
      console.log('🔍 useQuery queryFn called with isMember:', isMemberResult);
      // Use all available course IDs if no specific courses are selected
      const courseIdsToUse = searchCourses.length > 0 ? searchCourses : courses.map(c => c.id);
      console.log('🔍 Using courseIds:', courseIdsToUse);
      const fetchedTeeTimes = await optimizedReservationsService.getTeeTimeAvailability(
        searchDate,
        viewMode === 'teesheet' ? 4 : searchPlayers, // Use 4 players for tee sheet view to show all tee times
        {
          timeOfDay: searchTimeOfDay === 'Both' ? '' : searchTimeOfDay,
          roundType: searchHoles === 'Both' ? '18-hole' : `${searchHoles}-hole`,
          courseIds: courseIdsToUse,
          customerTypeId: getCustomerTypeIdString(authenticatedCustomer) ?? '',
        },
        true, // Always include member information to show names in grid and tee sheet views
        viewMode === 'teesheet' // Show all reservations in tee sheet view (including fully booked) so member names are visible
      );
      
      // Add course colors, names and pricing options to tee times
      const mapped = fetchedTeeTimes.map(teeTime => {
        const course = courses.find(c => c.id === teeTime.courseId);
        return {
          ...teeTime,
          courseName: course?.name || teeTime.courseName || 'Unknown Course',
          courseColor: course?.color,
          // Use real pricing options from the API (already included in teeTime.pricingOptions)
          // Override holeSet based on user's search selection
          holeSet: searchHoles === '9' ? '9' : '18'
        };
      });
      return filterBookableTeeTimesForOnlineSearch(searchDate, mapped);
    },
    enabled: !!searchDate && searchPlayers > 0 && courses.length > 0,
    staleTime: 5 * 1000, // 5 seconds for real-time availability updates
    refetchInterval: 10 * 1000, // Refetch every 10 seconds for better real-time updates
  });

  /** Desktop scroll-away: keep filters + headings fully visible whenever results aren’t in the scrollable list state. */
  const bookingDesktopScrollAwayChromeOpen = useMemo(
    () =>
      isMobile || isLoading || error != null || teeTimes.length === 0
        ? true
        : bookingTopChromeVisible,
    [isMobile, isLoading, error, teeTimes.length, bookingTopChromeVisible],
  );

  /** Prefer guest default from tee-time payload (fresh public course fetch in availability service). */
  const resolvedDefaultGuestCustomerTypeId = useMemo(() => {
    const tid = selectedTeeTime?.id;
    const live = tid && teeTimes.length ? teeTimes.find((t) => t.id === tid) : null;
    const tee = live ?? selectedTeeTime ?? selectedTeeTimeForPricing;
    if (tee?.courseDefaultGuestTypeId) return tee.courseDefaultGuestTypeId;
    const courseId = tee?.courseId ?? selectedTeeTime?.courseId ?? selectedTeeTimeForPricing?.courseId;
    if (courseId && courses.length > 0) {
      const course = courses.find((c) => c.id === courseId);
      if (course?.defaultGuestTypeId) return course.defaultGuestTypeId;
    }
    return defaultGuestCustomerTypeId ?? undefined;
  }, [
    selectedTeeTime,
    selectedTeeTimeForPricing,
    selectedTeeTime?.id,
    teeTimes,
    courses,
    defaultGuestCustomerTypeId,
  ]);

  // Fetch blocked tee times for tee sheet view
  // Result intentionally unused: this query only warms the blocked-tee-times cache.
  useQuery({
    queryKey: ['blockedTeeTimes', searchDate, searchCourses, viewMode],
    queryFn: async () => {
      console.log('🔍 Fetching blocked tee times - searchCourses:', searchCourses, 'viewMode:', viewMode);
      
      // Use the first selected course, or default to the Links course if no course is selected
      const courseId = searchCourses.length > 0 ? searchCourses[0] : 'cmf705rv6000b2n6olj69ffym'; // Links course ID
      console.log('🔍 Using course ID for blocked times:', courseId);
      
      try {
        const dateStr = searchDate.toISOString().split('T')[0];
        const url = `${getApiBaseUrl()}/tee-blocks?courseId=${courseId}&date=${dateStr}`;
        console.log('🔍 Fetching blocked times from:', url);
        
        const response = await fetch(url);
        console.log('🔍 Response status:', response.status);
        
        if (response.ok) {
          const blockedTimes = await response.json();
          console.log('🔍 Fetched blocked tee times for tee sheet view:', blockedTimes);
          
          // Convert to Set format for TeeSheetView
          const blockedSet = new Set<string>();
          blockedTimes.forEach((block: any) => {
            const d = new Date(block.startsAt);
            const time = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
            // Add all 4 spots for each blocked time
            for (let spot = 1; spot <= 4; spot++) {
              blockedSet.add(`${time}-${spot}`);
            }
          });
          
          console.log('🔍 Converted blocked times to Set:', Array.from(blockedSet));
          setBlockedTeeTimes(blockedSet);
          return blockedTimes;
        } else {
          console.error('🔍 Failed to fetch blocked times, status:', response.status);
        }
        return [];
      } catch (error) {
        console.error('Error fetching blocked tee times:', error);
        return [];
      }
    },
    enabled: !!searchDate && viewMode === 'teesheet'
  });

  // Listen for reservation changes to refresh tee time availability
  useEffect(() => {
    const handleReservationChange = () => {
      console.log('🔄 Online booking: Reservation change detected, refreshing tee time availability...');
      refetch();
    };

    window.addEventListener('reservation-created', handleReservationChange);
    window.addEventListener('reservation-updated', handleReservationChange);
    window.addEventListener('reservation-deleted', handleReservationChange);
    window.addEventListener('force-refresh-teesheet', handleReservationChange);

    return () => {
      window.removeEventListener('reservation-created', handleReservationChange);
      window.removeEventListener('reservation-updated', handleReservationChange);
      window.removeEventListener('reservation-deleted', handleReservationChange);
      window.removeEventListener('force-refresh-teesheet', handleReservationChange);
    };
  }, [refetch]);

  // Listen for coordinated events from admin app
  useEffect(() => {
    const handleCoordinatedChange = (event: CustomEvent) => {
      // Only `source` is used below; the rest of the payload is intentionally ignored.
      const { source } = event.detail;
      
      // Skip if this came from the same app to prevent loops
      if (source === 'booking') return;
      
      console.log('🔄 Booking received coordinated event from admin:', event.detail);
      
      // Invalidate React Query cache with delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['teeTimes'] });
      }, 300);
    };

    window.addEventListener('coordinated-reservation-change', handleCoordinatedChange as EventListener);
    
    return () => {
      window.removeEventListener('coordinated-reservation-change', handleCoordinatedChange as EventListener);
    };
  }, [queryClient]);

  // Handle OAuth callback — open modal so LoginRegisterModal can exchange code (depends on search string).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const id_token = params.get('id_token');
    const state = params.get('state');

    if ((code || id_token) && state) {
      console.log('🔄 OAuth callback detected, opening login modal');
      setShowLoginModal(true);
    }
  }, [location.search]);

  // iOS Safari back-forward cache: re-open modal when restored from Google redirect.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      if (isBookingOAuthReturn(window.location.search)) {
        setShowLoginModal(true);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Fetch all products (needed for dynamic product matching) - filtered by club
  const { data: allProductsData, error: allProductsError } = useQuery<any>({
    queryKey: ['all-products', clubIdOrNumber],
    queryFn: () => productsService.getAll(clubIdOrNumber),
    enabled: !!clubIdOrNumber,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Fetch golf-round products separately (they're excluded from the general products list)
  // Uses PUBLIC endpoint with club filter - no auth required
  const { data: golfRoundProductsData } = useQuery<any>({
    queryKey: ['golf-round-products', clubIdOrNumber],
    queryFn: async () => {
      try {
        const qs = publicBookingApiParamsFromClubRouteKey(clubIdOrNumber);
        qs.set('category', 'golf-round');
        const response = await fetch(`${getApiBaseUrl()}/products/public/list?${qs.toString()}`);
        if (!response.ok) {
          throw new Error('Failed to fetch golf-round products');
        }
        return await response.json();
      } catch (error) {
        console.warn('Failed to fetch golf-round products:', error);
        return { products: [] };
      }
    },
    enabled: !!clubIdOrNumber,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Merge all products with golf-round products (memoized — unstable refs retrigger checkout fetches)
  const allProducts = useMemo(() => {
    const base = Array.isArray(allProductsData?.products) ? allProductsData.products : EMPTY_PRODUCTS;
    const golf = Array.isArray(golfRoundProductsData?.products) ? golfRoundProductsData.products : EMPTY_PRODUCTS;
    if (base === EMPTY_PRODUCTS && golf === EMPTY_PRODUCTS) return EMPTY_PRODUCTS;
    if (golf === EMPTY_PRODUCTS) return base as Product[];
    if (base === EMPTY_PRODUCTS) return golf as Product[];
    return [...base, ...golf] as Product[];
  }, [allProductsData, golfRoundProductsData]);

  // Fetch available extras - filtered by club
  const { data: extrasData, error: extrasError } = useQuery<Product[]>({
    queryKey: ['extras', clubIdOrNumber],
    queryFn: () => productsService.getExtrasProducts(clubIdOrNumber),
    enabled: !!clubIdOrNumber,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Ensure extras is always an array (memoized for stable effect deps)
  const extras = useMemo(
    () => (Array.isArray(extrasData) ? extrasData : EMPTY_PRODUCTS),
    [extrasData]
  );

  const golfRoundProductIds = useMemo(
    () => allProducts.filter((p) => p.category === 'golf-round').map((p) => p.id),
    [allProducts]
  );

  const checkoutPerPlayerGreenFees = useMemo(() => {
    if (!selectedTeeTime) return [] as number[];
    const opts = selectedTeeTime.pricingOptions;
    const booker = selectedTeeTime.price ?? 0;
    const fees: number[] = [booker];
    for (let pn = 2; pn <= searchPlayers; pn++) {
      const sm = selectedMembers.find((s) => s.playerNumber === pn);
      if (sm) {
        const tid = getLinkedMemberGreenFeeCustomerTypeId(selectedTeeTime, sm.member, isMemberResult);
        fees.push(resolveGreenFeePriceFromOptions(opts, tid, booker));
      } else {
        const tid = companionGuestCustomerTypeByPlayer[pn];
        fees.push(resolveGreenFeePriceFromOptions(opts, tid, booker));
      }
    }
    return fees;
  }, [
    selectedTeeTime,
    searchPlayers,
    selectedMembers,
    companionGuestCustomerTypeByPlayer,
    isMemberResult,
  ]);

  useEffect(() => {
    if (!showCheckout || !selectedTeeTime) return;
    const opts = selectedTeeTime.pricingOptions;
    if (!opts?.length) return;

    setCompanionGuestCustomerTypeByPlayer((prev) => {
      const next = { ...prev };
      for (let pn = 2; pn <= searchPlayers; pn++) {
        const hasMember = selectedMembers.some((sm) => sm.playerNumber === pn);
        if (hasMember) {
          delete next[pn];
          continue;
        }
        if (!next[pn]) {
          const fallbackId = resolveGuestCustomerTypeIdFromPricingOptions(
            opts,
            resolvedDefaultGuestCustomerTypeId,
          );
          if (fallbackId) next[pn] = fallbackId;
        }
      }
      Object.keys(next).forEach((k) => {
        const num = Number(k);
        if (num > searchPlayers || num < 2) delete next[num];
      });
      return next;
    });
  }, [
    showCheckout,
    selectedTeeTime?.id,
    searchPlayers,
    selectedMembers,
    resolvedDefaultGuestCustomerTypeId,
    selectedTeeTime,
  ]);

  const linkedSlotLabelsForModal = useMemo(() => {
    const o: Record<number, string> = {};
    for (const sm of selectedMembers) {
      o[sm.playerNumber] =
        `${sm.member?.firstName ?? ''} ${sm.member?.lastName ?? ''}`.trim() || 'Member';
    }
    return o;
  }, [selectedMembers]);

  const bookerSlotLabelForModal = useMemo(() => {
    if (!authenticatedCustomer) return undefined;
    const n =
      `${authenticatedCustomer.firstName ?? ''} ${authenticatedCustomer.lastName ?? ''}`.trim();
    return n || undefined;
  }, [authenticatedCustomer]);

  const initialCustomerTypeByPlayerForModal = useMemo(():
    | Record<number, string>
    | undefined => {
    if (!showCheckout || !selectedTeeTime?.pricingOptions?.length || !selectedTeeTimeForPricing) {
      return undefined;
    }
    if (selectedTeeTime.id !== selectedTeeTimeForPricing.id) {
      return undefined;
    }
    const opts = selectedTeeTime.pricingOptions;
    const out: Record<number, string> = {};
    const p1 = selectedTeeTime.customerTypeId;
    if (p1) out[1] = p1;

    for (let pn = 2; pn <= searchPlayers; pn++) {
      const sm = selectedMembers.find((s) => s.playerNumber === pn);
      if (sm) {
        const tid = getLinkedMemberGreenFeeCustomerTypeId(
          selectedTeeTime,
          sm.member,
          isMemberResult
        );
        if (tid) out[pn] = tid;
      } else {
        const tid =
          companionGuestCustomerTypeByPlayer[pn] ||
          resolveGuestCustomerTypeIdFromPricingOptions(opts, resolvedDefaultGuestCustomerTypeId);
        if (tid) out[pn] = tid;
      }
    }
    return out;
  }, [
    showCheckout,
    selectedTeeTime,
    selectedTeeTimeForPricing,
    searchPlayers,
    selectedMembers,
    companionGuestCustomerTypeByPlayer,
    isMemberResult,
    resolvedDefaultGuestCustomerTypeId,
  ]);

  const checkoutPricing = useMemo(
    () =>
      getPassAwareCheckoutPricing(
        selectedTeeTime,
        searchPlayers,
        cartAssignments,
        extras,
        extrasAssignments,
        allProducts,
        appliedPass,
        checkoutPerPlayerGreenFees.length === searchPlayers ? checkoutPerPlayerGreenFees : null
      ),
    [
      selectedTeeTime,
      searchPlayers,
      cartAssignments,
      extras,
      extrasAssignments,
      allProducts,
      appliedPass,
      checkoutPerPlayerGreenFees,
    ]
  );

  // Fetch coupons applicable to this booking (needs checkoutPricing — must run after its useMemo)
  useEffect(() => {
    if (showConfirmationModal) return;
    if (!selectedTeeTime) {
      setAvailableCoupons([]);
      return;
    }

    let cancelled = false;

    const fetchCoupons = async () => {
      try {
        const coupons = await couponService.getAvailableCouponsForBooking({
          customerId: authenticatedCustomer?.id,
          customerTypeId: getCustomerTypeIdString(authenticatedCustomer),
          totalAmount:
            checkoutPricing?.fallbackSubtotalBeforeCoupon ??
            (selectedTeeTime.price || 0) * searchPlayers,
          selectedDate: searchDate,
          selectedTime: selectedTeeTime.dateTime.toTimeString().slice(0, 5),
          productCategories: ['golf-round'],
          productIds: golfRoundProductIds,
        });
        if (cancelled) return;
        setAvailableCoupons(coupons);
        console.log('🔍 Fetched booking-applicable coupons:', coupons.length);
      } catch (error) {
        if (cancelled) return;
        console.error('🔍 Error fetching coupons:', error);
        setAvailableCoupons([]);
      }
    };

    void fetchCoupons();
    return () => {
      cancelled = true;
    };
  }, [
    showConfirmationModal,
    selectedTeeTime?.id,
    selectedTeeTime?.price,
    selectedTeeTime?.dateTime,
    searchDate,
    searchPlayers,
    authenticatedCustomer?.id,
    checkoutPricing?.fallbackSubtotalBeforeCoupon,
    golfRoundProductIds,
  ]);
  
  // Log allProducts fetching status
  React.useEffect(() => {
    if (allProductsError) {
      console.error('❌ Error fetching all products:', allProductsError);
    } else if (allProductsData) {
      console.log('✅ All products fetched successfully:', allProducts.length, 'products');
    }
  }, [allProductsData, allProductsError, allProducts]);

  // Log extras fetching status
  React.useEffect(() => {
    if (extrasError) {
      console.error('❌ Error fetching extras:', extrasError);
    } else if (extrasData) {
      console.log('✅ Extras fetched successfully:', extras);
    }
  }, [extrasData, extrasError, extras]);

  // Fetch club details including policies
  const { data: clubDetails, error: clubDetailsError } = useQuery({
    queryKey: ['clubDetails', clubIdOrNumber],
    queryFn: async () => {
      try {
        // First try to get club from courses - MUST pass clubIdOrNumber
        if (!clubIdOrNumber) {
          console.error('❌ clubId or clubNumber is required to fetch club details');
          return null;
        }
        const courses = await golfClubsService.getCoursesWithTemplates(clubIdOrNumber);
        console.log('🔍 Courses data received:', courses);
        
        // Get the club from the first course that has club information
        const courseWithClub = courses.find(course => course.club && course.club.name);
        if (courseWithClub?.club) {
          console.log('✅ Selected club from course:', courseWithClub.club.name);
          return courseWithClub.club;
        }
        
        // Fallback: fetch clubs directly
        console.log('🔍 No club found in courses, fetching clubs directly...');
        const clubs = await golfClubsService.getClubInfo();
        console.log('🔍 Clubs fetched:', clubs);
        
        // Return the first active club
        const activeClub = clubs.find(club => club.status === 'active') || clubs[0];
        if (activeClub) {
          console.log('✅ Selected club:', activeClub.name);
          return activeClub;
        }
        
        console.warn('⚠️ No club found');
        return null;
      } catch (error: any) {
        console.error('❌ Error fetching club details:', error);
        throw error;
      }
    },
    enabled: !!clubIdOrNumber,
    staleTime: 60_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // Log club details for debugging
  useEffect(() => {
    if (clubDetails) {
      console.log('✅ Club details loaded:', clubDetails);
    }
    if (clubDetailsError) {
      console.error('❌ Error loading club details:', clubDetailsError);
    }
  }, [clubDetails, clubDetailsError]);

  /**
   * Mandatory carts must follow live `/pricing/public/tee-times` (same as card prices), not stale course cache.
   * Merge in the latest row from `teeTimes` by id so checkout updates when the list refetches after admin changes.
   */
  const checkoutTeeForCartPolicy = useMemo(() => {
    if (!selectedTeeTime?.id) return selectedTeeTime;
    const live = teeTimes.find((t) => t.id === selectedTeeTime.id);
    return live ?? selectedTeeTime;
  }, [selectedTeeTime, teeTimes]);

  const isMandatoryCartsForCheckout = !!(
    checkoutTeeForCartPolicy?.priceIncludesHalfCartAddon ??
    checkoutTeeForCartPolicy?.mandatoryHalfCartPreload
  );

  const preloadHalfCartWithGreenFeeByCourseId = useMemo(() => {
    const m: Record<string, boolean> = {};
    for (const c of courses) {
      if (c.preloadHalfCartWithGreenFee) m[c.id] = true;
    }
    return m;
  }, [courses]);

  useEffect(() => {
    if (!showCheckout || !selectedTeeTime || !isMandatoryCartsForCheckout) return;
    const allPlayerIndices = Array.from({ length: searchPlayers }, (_, i) => i);
    setCartAssignments(allPlayerIndices);
  }, [showCheckout, selectedTeeTime?.id, searchPlayers, isMandatoryCartsForCheckout]);

  const prevMandatoryCartsRef = useRef(false);
  useEffect(() => {
    if (!showCheckout) {
      prevMandatoryCartsRef.current = isMandatoryCartsForCheckout;
      return;
    }
    if (prevMandatoryCartsRef.current && !isMandatoryCartsForCheckout) {
      setCartAssignments([]);
    }
    prevMandatoryCartsRef.current = isMandatoryCartsForCheckout;
  }, [showCheckout, isMandatoryCartsForCheckout]);

  useEffect(() => {
    if (isMandatoryCartsForCheckout) {
      setOpenCartDropdown(false);
    }
  }, [isMandatoryCartsForCheckout]);

  const handleSearch = (date: Date, players: number) => {
    setSearchDate(date);
    setSearchPlayers(players);
    // Keep existing filters for time of day, holes, and courses
  };


  const handleBackToSearch = () => {
    setShowCheckout(false);
    setSelectedTeeTime(null);
    setCompanionGuestCustomerTypeByPlayer({});
    // Reset assignments when going back
    setCartAssignments([]);
    // Clear customer message when going back to search
    setCustomerMessage('');
    setExtrasAssignments({});
    // Clear messages
    setReservationError(null);
  };

  // Player toggle handlers - direct assignment without quantities
  const handlePlayerToggle = (itemId: string, playerIndex: number) => {
    if (itemId === 'cart' && isMandatoryCartsForCheckout) {
      return;
    }
    if (itemId === 'cart') {
      setCartAssignments(prev => {
        const isAssigned = prev.includes(playerIndex);
        return isAssigned 
          ? prev.filter(p => p !== playerIndex)
          : [...prev, playerIndex].sort();
      });
    } else {
      setExtrasAssignments(prev => {
        const currentAssignments = prev[itemId] || [];
        const isAssigned = currentAssignments.includes(playerIndex);
        const newAssignments = isAssigned
          ? currentAssignments.filter(p => p !== playerIndex)
          : [...currentAssignments, playerIndex].sort();
        
        return {
          ...prev,
          [itemId]: newAssignments
        };
      });
    }
    
    // Trigger tax calculation after a short delay when user makes changes
    setTimeout(() => {
      if (selectedTeeTime) {
        calculateTaxes();
      }
    }, 100);
  };

  // Calculate taxes whenever cart or extras assignments change
  const calculateTaxes = React.useCallback(async () => {
    if (!selectedTeeTime) return;

    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Tax calculation timeout')), 3000); // Reduced to 3 seconds
      });
      
      const taxCalculationPromise = (async () => {
        const pricing = checkoutPricing;
        if (!pricing?.golfRoundProduct) {
          const hc = pricing?.holeCount ?? (selectedTeeTime.holeSet === '18-hole' || selectedTeeTime.holeSet === '18' ? 18 : 9);
          console.error(`🔍 Green Fee product not found for ${hc} holes`);
          throw new Error(`Green fee product not found for ${hc} holes`);
        }

        const products: Array<{ productId: string; unitPrice: number; quantity: number }> = [];

        if (pricing.perPlayerGreenFees && pricing.perPlayerGreenFees.length === searchPlayers) {
          const merge = new Map<number, number>();
          for (let i = 0; i < searchPlayers; i++) {
            const fee =
              pricing.coversGreenFee && i === 0 ? 0 : Number(pricing.perPlayerGreenFees[i]) || 0;
            merge.set(fee, (merge.get(fee) ?? 0) + 1);
          }
          for (const [unitPrice, quantity] of merge) {
            if (quantity > 0) {
              products.push({
                productId: pricing.golfRoundProduct.id,
                unitPrice,
                quantity,
              });
            }
          }
        } else {
          if (pricing.fullPricePlayers > 0) {
            products.push({
              productId: pricing.golfRoundProduct.id,
              unitPrice: pricing.greenUnit,
              quantity: pricing.fullPricePlayers
            });
          }
          if (pricing.coveredGreenPlayers > 0) {
            products.push({
              productId: pricing.golfRoundProduct.id,
              unitPrice: 0,
              quantity: pricing.coveredGreenPlayers
            });
          }
        }

        if (cartAssignments.length > 0) {
          if (!pricing.cartProduct) {
            console.error(`🔍 Cart product not found for ${pricing.holeCount} holes`);
            throw new Error(`Cart product not found for ${pricing.holeCount} holes`);
          }
          if (pricing.chargedCarts > 0) {
            products.push({
              productId: pricing.cartProduct.id,
              unitPrice: pricing.cartCatalogUnit,
              quantity: pricing.chargedCarts
            });
          }
        }

        if (Array.isArray(extras)) {
          extras.forEach(extra => {
            const assignments = extrasAssignments[extra.id] || [];
            if (assignments.length > 0) {
              products.push({
                productId: extra.id,
                unitPrice: extra.price || 0,
                quantity: assignments.length
              });
            }
          });
        } else {
          console.warn('⚠️ Extras is not an array:', extras);
        }

        const taxResult = await taxCalculationService.calculateProductsTax(products, clubIdOrNumber);
        setTaxCalculation(taxResult);
      })();
      
      // Race between tax calculation and timeout
      await Promise.race([taxCalculationPromise, timeoutPromise]);
    } catch (error) {
      console.error('❌ Error calculating taxes:', error);
      if (error instanceof Error && error.message === 'Tax calculation timeout') {
        // Set a fallback tax calculation
        setTaxCalculation({
          subtotal: 0,
          taxAmount: 0,
          total: 0,
          taxBreakdown: []
        });
      } else {
        setTaxCalculation(null);
      }
    }
  }, [checkoutPricing, selectedTeeTime, searchPlayers, cartAssignments, extrasAssignments, extras, clubIdOrNumber]);

  const handlePerPlayerRatesModalClose = useCallback(() => {
    setShowPerPlayerRatesModal(false);
    setSelectedTeeTimeForPricing(null);
  }, []);

  const handlePerPlayerRatesConfirm = useCallback(
    (selections: Record<number, MultiPlayerRatePick>) => {
      if (!selectedTeeTimeForPricing) return;
      const p1 = selections[1];
      if (!p1) return;

      setSelectedTeeTime({
        ...selectedTeeTimeForPricing,
        price: p1.price,
        customerTypeId: p1.customerTypeId,
      });

      setCompanionGuestCustomerTypeByPlayer((prev) => {
        const next = { ...prev };
        for (let pn = 2; pn <= searchPlayers; pn++) {
          if (selectedMembers.some((sm) => sm.playerNumber === pn)) continue;
          const sel = selections[pn];
          if (sel) next[pn] = sel.customerTypeId;
        }
        return next;
      });

      setShowPerPlayerRatesModal(false);
      setSelectedTeeTimeForPricing(null);

      setTimeout(() => {
        calculateTaxes();
      }, 200);

      if (!authenticatedCustomer) {
        setShowLoginModal(true);
        return;
      }

      if (isMemberResult) {
        const sessionData = localStorage.getItem('booking_service_auth');
        if (!sessionData) {
          setShowLoginModal(true);
          return;
        }
        try {
          const session = JSON.parse(sessionData);
          if (!session.token || Date.now() > session.expiresAt) {
            setShowLoginModal(true);
            return;
          }
        } catch {
          setShowLoginModal(true);
          return;
        }
      }

      setShowCheckout(true);
    },
    [
      selectedTeeTimeForPricing,
      searchPlayers,
      selectedMembers,
      authenticatedCustomer,
      isMemberResult,
      calculateTaxes,
    ]
  );

  // Calculate taxes when dependencies change with proper debouncing
  React.useEffect(() => {
    if (selectedTeeTime) {
      const timeoutId = setTimeout(() => {
        calculateTaxes();
      }, 300); // Reduced debounce time for better responsiveness
      return () => clearTimeout(timeoutId);
    }
  }, [selectedTeeTime, checkoutPricing, calculateTaxes]);

  // Coupon handlers
  const handleApplyCoupon = async () => {
    setCouponApplicabilityHint(null);
    if (!promoCode.trim() || !selectedTeeTime) {
      setCouponValidationResult({
        isValid: false,
        errorMessage: 'Please enter a coupon code and select a tee time'
      });
      return;
    }

    try {
      // Collect all golf-round product IDs so product-based restrictions work
      const golfRoundProductIds = allProducts
        .filter(p => p.category === 'golf-round')
        .map(p => p.id);

      const result = await couponService.validateCoupon(promoCode.trim(), {
        customerId: authenticatedCustomer?.id,
        customerTypeId: getCustomerTypeIdString(authenticatedCustomer),
        totalAmount:
          checkoutPricing?.fallbackSubtotalBeforeCoupon ??
          (selectedTeeTime.price || 0) * searchPlayers,
        selectedDate: searchDate,
        selectedTime: selectedTeeTime.dateTime.toTimeString().slice(0, 5),
        productCategories: ['golf-round'],
        productIds: golfRoundProductIds
      });

      const part = partitionCouponValidationForCheckoutUi(result);

      if (part.appliedPayload?.isValid && part.appliedPayload.coupon) {
        setCouponValidationResult(null);
        setCouponApplicabilityHint(null);
        setAppliedCoupon(part.appliedPayload.coupon);
        setCouponDiscount(part.appliedPayload.discountAmount || 0);
        setPromoCode(''); // Clear the input
        setShowCouponDropdown(false);
      } else {
        setCouponValidationResult(part.errorBanner);
        setCouponApplicabilityHint(part.applicabilityHint);
      }
    } catch (error) {
      console.error('🔍 Error applying coupon:', error);
      setCouponApplicabilityHint(null);
      setCouponValidationResult({
        isValid: false,
        errorMessage: 'Failed to validate coupon. Please try again.'
      });
    }
  };

  const handleSelectCoupon = async (coupon: Coupon) => {
    setCouponApplicabilityHint(null);
    setPromoCode(coupon.code);
    setShowCouponDropdown(false);
    setShowPassDropdown(false);
    // Auto-apply the selected coupon
    // Collect all golf-round product IDs so product-based restrictions work
    const golfRoundProductIds = allProducts
      .filter(p => p.category === 'golf-round')
      .map(p => p.id);

    const result = await couponService.validateCoupon(coupon.code, {
      customerId: authenticatedCustomer?.id,
      customerTypeId: getCustomerTypeIdString(authenticatedCustomer),
      totalAmount:
        checkoutPricing?.fallbackSubtotalBeforeCoupon ??
        (selectedTeeTime?.price ? selectedTeeTime.price * searchPlayers : 0),
      selectedDate: searchDate,
      selectedTime: selectedTeeTime?.dateTime ? selectedTeeTime.dateTime.toTimeString().slice(0, 5) : '',
      productCategories: ['golf-round'],
      productIds: golfRoundProductIds
    });

    const part = partitionCouponValidationForCheckoutUi(result);

    if (part.appliedPayload?.isValid && part.appliedPayload.coupon) {
      setCouponValidationResult(null);
      setCouponApplicabilityHint(null);
      setAppliedCoupon(part.appliedPayload.coupon);
      setCouponDiscount(part.appliedPayload.discountAmount || 0);
    } else {
      setCouponValidationResult(part.errorBanner);
      setCouponApplicabilityHint(part.applicabilityHint);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponValidationResult(null);
    setCouponApplicabilityHint(null);
    setPromoCode('');
  };

  // Helper to calculate discount for a given pass
  const calculatePassDiscount = (pass: any): number => {
    if (!pass) return 0;

    const passCategory = pass.passProduct?.category || '';
    const includedProducts = pass.passProduct?.includedProducts || [];

    // CRITICAL SECURITY FIX: Only discount products explicitly listed in includedProducts
    // NO CATEGORY FALLBACK - passes must have specific products configured
    if (!includedProducts || !Array.isArray(includedProducts) || includedProducts.length === 0) {
      console.warn(`⚠️ SECURITY WARNING: Pass ${passCategory} has no includedProducts configured. Pass will NOT discount any products.`);
      console.warn(`   This pass must be configured with specific products in includedProducts to work correctly.`);
      return 0;
    }

    console.log('🎫 Pass includedProducts:', includedProducts);

    // Calculate discount based on products that match the pass's includedProducts
    // We need to check which products are in the current reservation/cart
    let discountAmount = 0;

    // Check green fee product
    if (selectedTeeTime) {
      const is18Hole = selectedTeeTime.holeSet === '18-hole' || selectedTeeTime.holeSet === '18';
      const holeCount = is18Hole ? 18 : 9;
      
      // Find the green fee product
      const golfRoundProduct = allProducts.find(p => {
        const isGolfRound = p.category === 'golf-round';
        const nameMatches = 
          p.name === `${holeCount} Hole - Green Fee` ||
          p.name === `ROUND OF GOLF - ${holeCount} HOLES` ||
          (p.name.toLowerCase().includes(`${holeCount} hole`) && p.name.toLowerCase().includes('green fee')) ||
          (p.name.toLowerCase().includes(`${holeCount} hole`) && p.name.toLowerCase().includes('golf'));
        return isGolfRound && nameMatches;
      });

      // If green fee product is in includedProducts, add its price to discount
      if (golfRoundProduct && passCatalogProductMatchesInclusion(includedProducts, golfRoundProduct)) {
        const fromTee = (checkoutPerPlayerGreenFees[0] ?? selectedTeeTime.price) || 0;
        const fromPassRow = passDiscountUnitPrice(includedProducts, golfRoundProduct);
        const greenFeePrice = fromTee > 0 ? fromTee : fromPassRow;
        discountAmount += greenFeePrice;
        console.log('🎫 Green fee product matches pass includedProducts, adding to discount:', greenFeePrice);
      }
    }

    // Check cart products (if cart is assigned)
    if (cartAssignments.length > 0) {
      const is18Hole = selectedTeeTime?.holeSet === '18-hole' || selectedTeeTime?.holeSet === '18';
      const holeCount = is18Hole ? 18 : 9;

      const cartProducts = allProducts.filter(p => p.category === 'cart-rental');
      const productsForHoles = cartProducts.filter(p => {
        const name = p.name.toUpperCase();
        const is18 = name.includes('18 HOLES') || name.includes('18-HOLE');
        const is9 = name.includes('9 HOLES') || name.includes('9-HOLE');

        if (holeCount === 18) return is18;
        if (holeCount === 9) return is9;
        return false;
      });

      // Find cart product that matches includedProducts
      const matchingCartProduct = productsForHoles.find((p: any) =>
        passCatalogProductMatchesInclusion(includedProducts, p)
      );

      if (matchingCartProduct) {
        const cartPrice = passDiscountUnitPrice(includedProducts, matchingCartProduct);
        discountAmount += cartPrice;
        console.log('🎫 Cart product matches pass includedProducts, adding to discount:', cartPrice);
      }
    }

    // Check extras products
    if (Array.isArray(extras)) {
      for (const extra of extras) {
        const assignments = extrasAssignments[extra.id] || [];
        if (assignments.length > 0 && passCatalogProductMatchesInclusion(includedProducts, extra)) {
          const extraPrice = passDiscountUnitPrice(includedProducts, extra);
          discountAmount += extraPrice * assignments.length;
          console.log('🎫 Extra product matches pass includedProducts, adding to discount:', extraPrice * assignments.length);
        }
      }
    }

    console.log('🎫 Total pass discount calculated from includedProducts:', discountAmount);
    return discountAmount;

    /*
    // Legacy percentage / fixed-amount logic (kept for reference, but unused in
    // the new architecture where passes are unit-based). If we ever re-enable
    // this, we must ensure it doesn't conflict with full-coverage passes.

    // Generic handling for other pass types using savings fields
    // For percentage-based discounts, calculate based on the green fee price
    const savingsPercentage = parseFloat(pass.passProduct?.savingsPercentage?.toString() || '0');
    const savingsAmount = parseFloat(pass.passProduct?.savingsAmount?.toString() || '0');

    console.log('🎫 Pass discount fields:', {
      savingsPercentage,
      savingsAmount,
      passProductKeys: pass.passProduct ? Object.keys(pass.passProduct) : 'No passProduct'
    });

    let discountAmount = 0;

    if (savingsPercentage > 0) {
      // Calculate discount as percentage of the green fee price
      const greenFeePrice = selectedTeeTime?.price || 0;
      discountAmount = (greenFeePrice * savingsPercentage) / 100;
      console.log('🎫 Pass discount calculated as percentage:', {
        greenFeePrice,
        savingsPercentage,
        discountAmount
      });
    } else if (savingsAmount > 0) {
      // Use fixed amount discount
      discountAmount = savingsAmount;
      console.log('🎫 Pass discount using fixed amount:', discountAmount);
    }

    return discountAmount;
    */
  };

  // Fetch passes only when they can cover at least one line item in this checkout
  // (e.g. hide cart-only passes when no cart — must run after calculatePassDiscount exists).
  const cartAssignmentsKey = cartAssignments.join(',');
  const extrasAssignmentsKey = useMemo(
    () => JSON.stringify(extrasAssignments),
    [extrasAssignments]
  );
  const checkoutGreenFeesKey = checkoutPerPlayerGreenFees.join(',');

  useEffect(() => {
    if (showConfirmationModal) {
      return;
    }
    if (!authenticatedCustomer || !showCheckout || !selectedTeeTime) {
      setAvailablePasses([]);
      return;
    }

    let cancelled = false;

    const fetchAvailablePasses = async () => {
      console.log('🎫 Fetching passes for customer:', authenticatedCustomer.id);
      try {
        // Customer JWT route — `/passes/customers/passes` without an employee token is rejected in production.
        const passes = await passesService.getMyPasses('active');
        if (cancelled) return;

        const usable = passes.filter((p: any) => {
          const hasUnitsRemaining = (p.totalUsesRemaining ?? 0) > 0;
          const applicable = isPassApplicableToSelectedTeeTime(p);
          if (!hasUnitsRemaining || !applicable) {
            console.log('🎫 Filtering out pass (units/tee rules):', {
              passId: p.id,
              name: p.passProduct?.name,
              hasUnitsRemaining,
              applicable,
            });
            return false;
          }
          const discount = calculatePassDiscount(p);
          if (discount <= 0) {
            console.log('🎫 Filtering out pass — nothing in this checkout matches included products:', {
              passId: p.id,
              name: p.passProduct?.name,
            });
            return false;
          }
          return true;
        });

        if (cancelled) return;
        console.log('🎫 Usable passes after checkout matching:', usable.length);
        setAvailablePasses(usable);
      } catch (error) {
        if (cancelled) return;
        console.error('❌ Error fetching passes:', error);
        setAvailablePasses([]);
      }
    };

    void fetchAvailablePasses();
    return () => {
      cancelled = true;
    };
  }, [
    showConfirmationModal,
    authenticatedCustomer?.id,
    showCheckout,
    selectedTeeTime?.id,
    cartAssignmentsKey,
    checkoutGreenFeesKey,
    extrasAssignmentsKey,
    allProducts,
    extras,
  ]);

  // Prefill pass when at least one applies to this round; auto-apply when only one applicable pass.
  useEffect(() => {
    if (!showCheckout || !selectedTeeTime?.id) return;
    if (!availablePasses.length) return;
    if (passPrefillOptOutTeeIdRef.current === selectedTeeTime.id) return;

    const selectionStillValid =
      selectedPass && availablePasses.some((p: any) => p.id === selectedPass.id);

    if (!selectionStillValid) {
      const first = availablePasses[0];
      setSelectedPass(first);
      if (availablePasses.length === 1) {
        const discountAmount = calculatePassDiscount(first);
        setAppliedPass(first);
        setPassDiscount(discountAmount);
        console.log('🎫 Auto-applied sole applicable pass:', first.passProduct?.name, discountAmount);
      }
      return;
    }

    if (
      availablePasses.length === 1 &&
      appliedPass?.id === availablePasses[0].id &&
      selectedPass?.id === availablePasses[0].id
    ) {
      const discountAmount = calculatePassDiscount(appliedPass);
      setPassDiscount(discountAmount);
    }
  }, [
    showCheckout,
    selectedTeeTime?.id,
    selectedTeeTime?.holeSet,
    selectedTeeTime?.price,
    availablePasses,
    selectedPass?.id,
    appliedPass?.id,
    checkoutPerPlayerGreenFees,
    cartAssignments,
    extrasAssignments,
    allProducts,
    extras,
  ]);

  // Pass selection handlers
  const handlePassSelect = (passId: string) => {
    if (passId === 'none') {
      passPrefillOptOutTeeIdRef.current = selectedTeeTime?.id ?? null;
      setSelectedPass(null);
      setAppliedPass(null);
      setPassDiscount(0);
      return;
    }

    passPrefillOptOutTeeIdRef.current = null;

    const pass = availablePasses.find(p => p.id === passId);
    if (pass) {
      console.log('🎫 Pass selected (pending):', pass);
      console.log('🎫 Pass product:', pass.passProduct);
      setSelectedPass(pass);
    }
  };

  const handleApplyPass = () => {
    if (!selectedPass) {
      setAppliedPass(null);
      setPassDiscount(0);
      return;
    }

    const discountAmount = calculatePassDiscount(selectedPass);
    setAppliedPass(selectedPass);
    setPassDiscount(discountAmount);
    console.log('🎫 Final pass discount applied via Apply button:', discountAmount);
  };

  const handleRemovePass = () => {
    passPrefillOptOutTeeIdRef.current = selectedTeeTime?.id ?? null;
    setAppliedPass(null);
    setPassDiscount(0);
    setSelectedPass(null);
  };

  // Handle reservation confirmation
  const handleConfirmReservation = async () => {
    if (!selectedTeeTime) {
      setReservationError('No tee time selected');
      return;
    }

    // Security: Verify customer is authenticated before allowing reservation
    const sessionData = localStorage.getItem('booking_service_auth');
    if (!sessionData) {
      setReservationError('Authentication required to make reservations. Please log in first.');
      setShowLoginModal(true);
      return;
    }

    try {
      const session = JSON.parse(sessionData);
      if (!session.token) {
        setReservationError('Invalid authentication session. Please log in again.');
        setShowLoginModal(true);
        return;
      }

      // Security: Check if session is expired
      if (Date.now() > session.expiresAt) {
        setReservationError('Your session has expired. Please log in again.');
        setShowLoginModal(true);
        return;
      }

      console.log('🔐 Customer authenticated for reservation:', session.customer);
    } catch (error) {
      console.error('❌ Authentication validation failed:', error);
      setReservationError('Authentication validation failed. Please log in again.');
      setShowLoginModal(true);
      return;
    }

    // Validate tee time availability before creating reservation
    console.log('🔍 Validating tee time availability...');
    console.log('🔍 Tee time details:', {
      id: selectedTeeTime.id,
      availableSpots: selectedTeeTime.availableSpots,
      capacity: selectedTeeTime.capacity,
      bookedCount: selectedTeeTime.bookedCount,
      requestedPlayers: searchPlayers
    });

    // Check if tee time has enough available spots
    if (selectedTeeTime.availableSpots < searchPlayers) {
      const errorMessage = `Not enough available spots. Only ${selectedTeeTime.availableSpots} spots available, but ${searchPlayers} players requested.`;
      console.error('❌ Tee time validation failed:', errorMessage);
      setReservationError(errorMessage);
      setIsCreatingReservation(false);
      return;
    }

    // Check if tee time is blocked (availableSpots = 0 and bookedCount = 0 might indicate blocked)
    if (selectedTeeTime.availableSpots === 0 && selectedTeeTime.bookedCount === 0) {
      const errorMessage = 'This tee time is blocked and not available for booking.';
      console.error('❌ Tee time validation failed:', errorMessage);
      setReservationError(errorMessage);
      setIsCreatingReservation(false);
      return;
    }

    // Check if requested players exceed capacity
    if (searchPlayers > selectedTeeTime.capacity) {
      const errorMessage = `Too many players. Maximum capacity is ${selectedTeeTime.capacity} players, but ${searchPlayers} players requested.`;
      console.error('❌ Tee time validation failed:', errorMessage);
      setReservationError(errorMessage);
      setIsCreatingReservation(false);
      return;
    }

    console.log('✅ Tee time validation passed');
    console.log('🚀 Starting reservation creation...');
    setIsCreatingReservation(true);
    setReservationError(null);

    try {
      // 🚨 FIX: Calculate available spot numbers based on existing players
      const existingSpotNumbers = new Set(selectedTeeTime.memberPlayers?.map(p => p.spotNumber) || []);
      const maxSpots = selectedTeeTime.capacity || 4;
      const availableSpots: number[] = [];
      
      // Find all available spots (not occupied by existing players)
      for (let spot = 1; spot <= maxSpots; spot++) {
        if (!existingSpotNumbers.has(spot)) {
          availableSpots.push(spot);
        }
      }
      
      console.log('🔍 Spot assignment analysis:', {
        existingSpotNumbers: Array.from(existingSpotNumbers),
        availableSpots: availableSpots,
        requestedPlayers: searchPlayers,
        maxSpots: maxSpots,
        teeTimeId: selectedTeeTime.id,
        teeTimeDateTime: selectedTeeTime.dateTime
      });

      // Calculate actual number of players to book based on available spots
      const actualPlayerCount = Math.min(searchPlayers, selectedTeeTime.availableSpots);
      console.log('🔍 Player count calculation:', {
        requestedPlayers: searchPlayers,
        availableSpots: selectedTeeTime.availableSpots,
        actualPlayerCount
      });

      // Distribute coupon discount evenly across all players so that the admin
      // ReservationModal shows the same percentage per ticket instead of
      // putting the entire discount on Player 1.
      const perPlayerCouponDiscount =
        appliedCoupon && couponDiscount > 0
          ? couponDiscount / actualPlayerCount
          : 0;

      const perSlotGreenFees =
        checkoutPerPlayerGreenFees.length >= actualPlayerCount
          ? checkoutPerPlayerGreenFees.slice(0, actualPlayerCount)
          : Array.from({ length: actualPlayerCount }, () => selectedTeeTime.price || 0);

      // Create player slots for the reservation with product and discount assignments
      const playerSlots = await Promise.all(Array.from({ length: actualPlayerCount }, async (_, index) => {
        const products = [];
        
        // Add green fee for this player - ALWAYS add it even if price is 0 (for members)
        // This is critical for 18-hole on 9-hole course detection
        // Determine if it's 9-hole or 18-hole based on the actual tee time's holeSet property
        const is18Hole = selectedTeeTime.holeSet === '18-hole' || selectedTeeTime.holeSet === '18';
        const holeCount = is18Hole ? 18 : 9;
        
        // Find the correct green fee product dynamically (like admin does)
        const golfRoundProduct = allProducts.find(p => {
          const isGolfRound = p.category === 'golf-round';
          const nameMatches = 
            p.name === `${holeCount} Hole - Green Fee` ||
            p.name === `ROUND OF GOLF - ${holeCount} HOLES` ||
            (p.name.toLowerCase().includes(`${holeCount} hole`) && p.name.toLowerCase().includes('green fee')) ||
            (p.name.toLowerCase().includes(`${holeCount} hole`) && p.name.toLowerCase().includes('golf'));
          return isGolfRound && nameMatches;
        });
        
        if (!golfRoundProduct) {
          console.error(`🔍 Green Fee product not found for ${holeCount} holes`);
          throw new Error(`Green fee product not found for ${holeCount} holes`);
        }
        
        console.log(`🔍 Using green fee product: ${golfRoundProduct.name} (${golfRoundProduct.id})`);
        
        // Calculate tax for green fee (use price or 0 for members)
        const greenFeePrice = perSlotGreenFees[index] ?? (selectedTeeTime.price || 0);
        console.log(`📊 Calculating tax for green fee product ${golfRoundProduct.id} with price ${greenFeePrice}...`);
        const greenFeeTax = await taxCalculationService.calculateProductTax(
          golfRoundProduct.id,
          greenFeePrice,
          1,
          clubIdOrNumber,
        );
        console.log(`✅ Green fee tax calculated:`, greenFeeTax);
        
        products.push({
          productId: golfRoundProduct.id,
          productName: golfRoundProduct.name,
          productCategory: 'golf-round',
          quantity: 1,
          unitPrice: greenFeePrice,
          totalPrice: greenFeeTax.totalPrice,
          taxAmount: greenFeeTax.taxAmount
        });
        
        // Add cart if this player is assigned to cart
        if (cartAssignments.includes(index)) {
          // Use the appropriate cart product based on the actual tee time's holeSet property
          const is18Hole = selectedTeeTime.holeSet === '18-hole' || selectedTeeTime.holeSet === '18';
          const holeCount = is18Hole ? 18 : 9;
          
          // Find the correct cart product - use dynamic cart product selection
          const cartProducts = allProducts.filter(p => p.category === 'cart-rental');
          const productsForHoles = cartProducts.filter(p => {
            const name = p.name.toUpperCase();
            const is18Hole = name.includes('18 HOLES') || name.includes('18-HOLE');
            const is9Hole = name.includes('9 HOLES') || name.includes('9-HOLE');
            
            if (holeCount === 18) return is18Hole;
            if (holeCount === 9) return is9Hole;
            return false;
          });
          
          // Fallback priority: half > full > 1-person
          const fallbackOrder = ['HALF', 'FULL', '1-PERSON'];
          let cartProduct = null;
          
          for (const type of fallbackOrder) {
            cartProduct = productsForHoles.find(p => {
              const productType = p.name.toUpperCase().includes('HALF') ? 'HALF' : 
                                p.name.toUpperCase().includes('FULL') ? 'FULL' : 
                                p.name.toUpperCase().includes('1 PERSON') ? '1-PERSON' : 'UNKNOWN';
              return productType === type;
            });
            
            if (cartProduct) {
              console.log(`🔍 Selected cart product: ${cartProduct.name} (${type})`);
              break;
            }
          }
          
          if (!cartProduct) {
            console.error(`🔍 Cart product not found for ${holeCount} holes`);
            throw new Error(`Cart product not found for ${holeCount} holes`);
          }
          
          console.log(`🔍 Using cart product: ${cartProduct.name} (${cartProduct.id})`);
          
          // Calculate tax for cart
          console.log(`📊 Calculating tax for cart product ${cartProduct.id}...`);
          const cartPrice = cartProduct.price || 0;
          const cartTax = await taxCalculationService.calculateProductTax(
            cartProduct.id,
            cartPrice,
            1,
            clubIdOrNumber,
          );
          console.log(`✅ Cart tax calculated:`, cartTax);
          
          products.push({
            productId: cartProduct.id,
            productName: cartProduct.name,
            productCategory: 'cart-rental',
            quantity: 1,
            unitPrice: cartPrice,
            totalPrice: cartTax.totalPrice,
            taxAmount: cartTax.taxAmount
          });
        }
        
        // Add extras if this player is assigned to any extras
        if (Array.isArray(extras)) {
          for (const extra of extras) {
          const extraAssignments = extrasAssignments[extra.id] || [];
          if (extraAssignments.includes(index)) {
            // Calculate tax for extra
            const extraTax = await taxCalculationService.calculateProductTax(
              extra.id,
              extra.price || 0,
              1,
              clubIdOrNumber,
            );
            
            products.push({
              productId: extra.id,
              productName: extra.name,
              productCategory: 'extras',
              quantity: 1,
              unitPrice: extra.price || 0,
              totalPrice: extraTax.totalPrice,
              taxAmount: extraTax.taxAmount
            });
          }
        }
        }
        
        // Check if this player slot has a selected member
        // For index 0 (Player 1), we don't look for selected members since Player 1 is always the authenticated customer
        // For index 1+ (Player 2+), we look for selected members with playerNumber = index + 1
        const selectedMember = index === 0 ? null : selectedMembers.find(sm => sm.playerNumber === index + 1);
        const assignedSpotNumber = availableSpots[index]; // Use available spot, not index + 1
        
        console.log(`🔍 Player slot ${index + 1}: selectedMember =`, selectedMember, 'assigned to spot', assignedSpotNumber);
        
        if (index === 0 && authenticatedCustomer) {
          // First player is always the authenticated customer
          return {
            spotNumber: assignedSpotNumber,
            customerId: authenticatedCustomer.id,
            isGuest: false,
            guestName: undefined,
            notes: undefined,
            // Add pass data if a pass has been applied
            ...(appliedPass && {
              passSubscriptionId: appliedPass.id,
              passName: appliedPass.passProduct?.name,
              passDiscount: passDiscount
            }),
            // Add coupon data if a coupon has been applied
            ...(appliedCoupon && couponDiscount > 0 && {
              couponCode: appliedCoupon.code,
              couponDiscount: perPlayerCouponDiscount
            }),
            products: products.map(product => ({ 
              id: product.productId,
              productName: product.productName,
              productCategory: product.productCategory,
              quantity: product.quantity,
              unitPrice: product.unitPrice,
              totalPrice: product.totalPrice,
              taxAmount: product.taxAmount
            }))
          };
        } else if (selectedMember) {
          // This player slot has a selected member
          return {
            spotNumber: assignedSpotNumber,
            customerId: selectedMember.member.id,
            isGuest: false,
            guestName: undefined,
            notes: undefined,
            ...(appliedCoupon && couponDiscount > 0 && {
              couponCode: appliedCoupon.code,
              couponDiscount: perPlayerCouponDiscount
            }),
            products: products.map(product => ({ 
              id: product.productId,
              productName: product.productName,
              productCategory: product.productCategory,
              quantity: product.quantity,
              unitPrice: product.unitPrice,
              totalPrice: product.totalPrice,
              taxAmount: product.taxAmount
            }))
          };
        } else {
          // This player slot is a guest
          return {
            spotNumber: assignedSpotNumber,
            customerId: undefined, // Guest reservation
            isGuest: true,
            guestName: `Player ${index + 1}`,
            guestCustomerTypeId:
              companionGuestCustomerTypeByPlayer[index + 1] ||
              resolvedDefaultGuestCustomerTypeId ||
              'cmf6yxcn7004knfb0zj6l2r68', // Use slot rate or default guest customer type or fallback
            notes: undefined,
            ...(appliedCoupon && couponDiscount > 0 && {
              couponCode: appliedCoupon.code,
              couponDiscount: perPlayerCouponDiscount
            }),
            products: products.map(product => ({ 
              id: product.productId,
              productName: product.productName,
              productCategory: product.productCategory,
              quantity: product.quantity,
              unitPrice: product.unitPrice,
              totalPrice: product.totalPrice,
              taxAmount: product.taxAmount
            }))
          };
        }
      }));

      // Build reservation data
      // Determine hole type from tee time
      const holeType = (selectedTeeTime.holeSet === '9-hole' || selectedTeeTime.holeSet === '9') ? 9 : 18;
      
      const reservationData = {
        teeTimeId:
          selectedTeeTime.reservationTeeTimeId ||
          buildReservationTeeTimeId(
            selectedTeeTime.originalDate || selectedTeeTime.dateTime.toISOString().split('T')[0],
            selectedTeeTime.dateTime.toTimeString().slice(0, 5),
            selectedTeeTime.laneKey,
          ),
        courseId: selectedTeeTime.courseId,
        customerId: authenticatedCustomer?.id || 'cmf74jd4g0emk2n6okuyi4d5a', // Use authenticated customer ID
        playerSlots,
        notes: `HoleType: ${holeType}`, // Store hole type in notes so the tee sheet can display the badge
        source: 'online',
        channel: 'online-booking',
        paymentIntentId: onlinePaymentIntentId,
        setupIntentId: onlineSetupIntentId,
      };

      const teeTimeLocalIso = `${selectedTeeTime.originalDate || selectedTeeTime.dateTime.toISOString().split('T')[0]}T${selectedTeeTime.dateTime.toTimeString().slice(0, 5)}:00`;
      const timeHHmm = selectedTeeTime.dateTime.toTimeString().slice(0, 5);
      const paymentSlots = buildPaymentSlotsFromPlayerSlots(
        playerSlots,
        getCustomerTypeIdString(authenticatedCustomer) ?? undefined
      );

      if (authenticatedCustomer?.id && !paymentCompleted) {
        const paymentReq = await fetchPaymentRequirements({
          courseId: selectedTeeTime.courseId,
          teeTimeLocalIso,
          timeHHmm,
          customerId: authenticatedCustomer.id,
          playerSlots: paymentSlots,
        });
        setCheckoutPaymentRequirement(paymentReq.requirement);

        if (onlinePaymentRequirementNeedsCheckout(paymentReq.requirement)) {
          if (!onlinePaymentIntentId && !onlineSetupIntentId) {
            const intent = await createCheckoutIntent({
              courseId: selectedTeeTime.courseId,
              teeTimeId: reservationData.teeTimeId,
              teeTimeLocalIso,
              timeHHmm,
              customerId: authenticatedCustomer.id,
              playerSlots: paymentSlots,
            });
            setCheckoutIntentData(intent);
            setPaymentStepError(null);
            setIsCreatingReservation(false);
            return;
          }
        }
      }

      console.log('🔍 Creating reservation with data:', reservationData);
      console.log('🔍 Selected tee time details:', {
        id: selectedTeeTime.id,
        courseId: selectedTeeTime.courseId,
        dateTime: selectedTeeTime.dateTime,
        availableSpots: selectedTeeTime.availableSpots
      });
      console.log('🔍 Selected members for reservation:', selectedMembers);
      console.log('🔍 Player slots created:', playerSlots);

      // Create the reservation
      const reservation = await optimizedReservationsService.createReservation(reservationData);
      console.log('✅ Reservation created successfully:', reservation);

      const noteToStaff = customerMessage.trim();
      let staffMessageNote: { text: string; variant: 'success' | 'warning' } | undefined;
      if (noteToStaff) {
        try {
          await optimizedReservationsService.createMessage(reservation.id, {
            message: noteToStaff
          });
          staffMessageNote = {
            variant: 'success',
            text: 'Your note to the pro shop was sent successfully.'
          };
        } catch (error) {
          console.error('❌ Failed to send customer message:', error);
          const detail =
            error instanceof ReservationsApiError
              ? error.message
              : error instanceof Error
                ? error.message
                : 'Please try again or contact the club.';
          staffMessageNote = {
            variant: 'warning',
            text: `Your tee time is reserved, but your note could not be sent: ${detail}`
          };
        }
      }

      // Show animated confirmation modal only if we're on the checkout page
      if (showCheckout) {
        const pricing = getPassAwareCheckoutPricing(
          selectedTeeTime,
          searchPlayers,
          cartAssignments,
          extras,
          extrasAssignments,
          allProducts,
          appliedPass,
          checkoutPerPlayerGreenFees.length === searchPlayers ? checkoutPerPlayerGreenFees : null
        );
        const totalPrice = taxCalculation
          ? Math.max(0, taxCalculation.total - couponDiscount)
          : Math.max(0, (pricing?.fallbackSubtotalBeforeCoupon ?? 0) - couponDiscount);
        
        // Format date to avoid timezone issues - use the original date string from backend
        const dateToUse = selectedTeeTime.originalDate || selectedTeeTime.dateTime.toISOString().split('T')[0];
        const [year, month, day] = dateToUse.split('-');
        const formattedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        const timeLabel = selectedTeeTime.dateTime.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
        const successSubtitle = `You're booked for ${formattedDate} at ${timeLabel} on ${selectedTeeTime.courseName} (${searchPlayers} ${searchPlayers === 1 ? 'player' : 'players'}).`;

        setPartnerInviteUrl(null);
        setPartnerInviteError(null);
        setPartnerInviteLoading(false);

        setReservationDetails({
          teeTime: selectedTeeTime.dateTime.toTimeString().slice(0, 5), // HH:MM format
          course: selectedTeeTime.courseName,
          date: formattedDate,
          players: searchPlayers,
          price: Math.round(totalPrice * 100) / 100,
          reservationId: reservation.id,
          mobilePassAccessToken: reservation.mobilePassAccessToken,
          successSubtitle,
          staffMessageNote
        });
        setShowConfirmationModal(true);
      }
      
      // Clear customer message after successful reservation
      setCustomerMessage('');
      setReservationError(null); // Clear any previous errors

      // Dispatch global event for immediate UI updates (same as offline system)
      window.dispatchEvent(new CustomEvent('reservation-created', {
        detail: { reservationData: reservation }
      }));
      reservationsService.invalidateMyReservationsCache();

    } catch (error: unknown) {
      console.error('❌ Error creating reservation:', error);

      let errorMessage = 'We could not complete your booking. Please try again in a moment.';
      if (error instanceof ReservationsApiError) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          errorMessage = 'The request took too long. Check your connection and try again.';
        } else if (error.message.includes('network')) {
          errorMessage = 'Network error. Check your connection and try again.';
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      setReservationError(errorMessage);
    } finally {
      setIsCreatingReservation(false);
    }
  };

  // Show loading when we have a club but brand settings haven't loaded yet (first visit, no cache)
  const showBrandLoading = !!clubIdOrNumber && !brandSettingsData && isBrandSettingsFetching;
  if (showBrandLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: tebox.colors.bgPrimary,
        fontFamily: 'var(--brand-font)',
        fontSize: teboxDesignSystem.typography.body.fontSize,
        lineHeight: 1.5,
        color: tebox.colors.textSecondary,
      }}>
        Loading...
      </div>
    );
  }

  // If showing checkout, render the full page checkout view
  if (showCheckout && selectedTeeTime) {
    return (
      <div 
        style={{ 
          backgroundColor: tebox.colors.bgPrimary,
          minHeight: isMobile ? '-webkit-fill-available' : '100vh',
          height: isMobile ? 'auto' : '100vh',
          overflowY: isMobile ? 'visible' : 'auto',
          WebkitOverflowScrolling: 'touch',
          position: 'relative',
          touchAction: 'pan-y pinch-zoom',
          overscrollBehavior: 'contain',
          willChange: isMobile ? 'auto' : 'scroll-position',
          // Safari mobile fixes
          WebkitTapHighlightColor: 'transparent',
          WebkitTouchCallout: 'none',
          // Hardware acceleration for smoother rendering
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          // Ensure margins/padding render correctly
          isolation: 'isolate',
          boxSizing: 'border-box'
        }}
      >
        {/* Checkout Header */}
        <div 
          style={{ 
            backgroundColor: tebox.colors.bgPrimary,
            paddingLeft: isMobile ? '24px' : '24px',
            paddingRight: isMobile ? '24px' : '24px',
            paddingTop: '16px',
            paddingBottom: '16px',
            // Force padding rendering in Chrome
            boxSizing: 'border-box'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 style={{
                fontFamily: bookingBrandFontFamilyVar,
                fontStyle: 'normal',
                fontWeight: '500',
                fontSize: teboxDesignSystem.typography.h3.fontSize,
                lineHeight: '21px',
                color: brandColors.textColor,
                margin: 0,
                marginTop: '15px'
              }}>Hello, {authenticatedCustomer?.firstName?.split('.')[0]?.split(' ')[0] || authenticatedCustomer?.firstName || 'Guest'}</h2>
            </div>
            <button
              onClick={handleBackToSearch}
              className="flex items-center gap-2 transition-colors"
              style={{
                fontFamily: bookingBrandFontFamilyVar,
                fontStyle: 'normal',
                fontWeight: '500',
                fontSize: isMobile ? '14px' : '14px',
                color: brandColors.textColor,
                backgroundColor: 'transparent',
                border: 'none',
                padding: isMobile ? '10px 14px' : '8px 14px',
                minHeight: isMobile ? '44px' : 'auto'
              }}
            >
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 20 20" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ transform: 'rotate(180deg)' }}
              >
                <path 
                  d="M4.16667 10H15.8333M15.8333 10L10.8333 5M15.8333 10L10.8333 15" 
                  stroke={brandColors.textColor}
                  strokeWidth={ICON_STROKE.standard} 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              <span>Back to Search</span>
            </button>
          </div>
        </div>

        {/* Checkout — two separate panels on highlight page (same tokens as before) */}
        <div
          className="flex w-full justify-center"
          style={
            isMobile
              ? {
                  marginTop: '16px',
                  marginBottom: '24px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  boxSizing: 'border-box',
                }
              : {
                  minHeight: 'calc(100vh - 120px)',
                  marginTop: '48px',
                  marginBottom: '32px',
                  paddingLeft: '32px',
                  paddingRight: '32px',
                  boxSizing: 'border-box',
                }
          }
        >
          <div
            className={`flex w-full max-w-[1600px] ${isMobile ? 'flex-col gap-4' : 'flex-row items-stretch gap-4'}`}
            style={{ boxSizing: 'border-box' }}
          >
            <div
              className={`${isMobile ? 'w-full' : 'flex-1'} min-w-0 bg-white rounded-[6px] overflow-hidden`}
              style={
                isMobile
                  ? { overflow: 'visible', boxSizing: 'border-box' }
                  : {
                      maxHeight: 'calc(100vh - 160px)',
                      overflowY: 'auto',
                      WebkitOverflowScrolling: 'touch',
                      touchAction: 'pan-y',
                      boxSizing: 'border-box',
                    }
              }
            >
              <div
                style={{
                  padding: isMobile ? '16px' : '24px',
                  boxSizing: 'border-box',
                }}
                className={isMobile ? 'space-y-4' : 'space-y-6'}
              >
            {/* Playing partners — flat list + row dividers (no nested cards) */}
            <div className="space-y-3">
              <div className={checkoutHighlightPanelClass}>
                <div style={{ marginBottom: isMobile ? '4px' : '2px' }}>
                  <h2
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontWeight: '500',
                      fontSize: isMobile ? '18px' : '20px',
                      lineHeight: isMobile ? '22px' : '21px',
                      color: brandColors.textColor,
                      margin: 0,
                    }}
                  >
                    Playing partners
                  </h2>
                  <p
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontWeight: '400',
                      fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                      lineHeight: 1.45,
                      color: brandColors.secondaryColor,
                      margin: '6px 0 0 0',
                      maxWidth: '40rem',
                    }}
                  >
                    {selectedTeeTime.pricingOptions?.length
                      ? 'Link playing partners or leave seats as guests.'
                      : 'Search to link playing partners, or add a note for open seats.'}
                  </p>
                </div>

                {(() => {
                  const partnerRowDivider = 'rgba(4, 42, 43, 0.07)';
                  const partnerMax = searchPlayers - 1;

                  return (
                    <>
                      {Array.from({ length: partnerMax }, (_, index) => {
                        const playerNumber = index + 2;
                        const selectedMember = selectedMembers.find(
                          (sm) => sm.playerNumber === playerNumber
                        );
                        const isLast = index === partnerMax - 1;

                        const searchField = !selectedMember && (
                          <div style={{ position: 'relative', width: '100%' }}>
                            <input
                              type="text"
                              placeholder={
                                isMemberResult ? 'Search members or add a note' : 'Name (optional)'
                              }
                              style={{
                                fontFamily: bookingBrandFontFamilyVar,
                                fontWeight: 400,
                                fontSize: isMobile ? '15px' : teboxDesignSystem.typography.body.fontSize,
                                minHeight: isMobile ? '44px' : '40px',
                                width: '100%',
                                padding: isMobile ? '12px 14px' : '8px 12px',
                                border: `1px solid ${tebox.colors.border}`,
                                borderRadius: 'var(--brand-border-radius, 6px)',
                                boxSizing: 'border-box',
                              }}
                              value={playerSearchQueries[playerNumber - 2] || ''}
                              onChange={(e) =>
                                handlePlayerInputChange(playerNumber - 2, e.target.value)
                              }
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = tebox.colors.focusRing;
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = tebox.colors.border;
                              }}
                            />
                            {isMemberResult &&
                              playerSearchQueries[playerNumber - 2] &&
                              playerSearchQueries[playerNumber - 2].length >= 1 && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    backgroundColor: '#FFFFFF',
                                    border: `1px solid ${tebox.colors.border}`,
                                    borderRadius: 'var(--brand-border-radius, 6px)',
                                    boxShadow: teboxDesignSystem.shadows.md,
                                    zIndex: 1000,
                                    maxHeight: '220px',
                                    overflowY: 'auto',
                                    marginTop: '4px',
                                  }}
                                >
                                  {isPlayerSearching[playerNumber - 2] ? (
                                    <div
                                      style={{
                                        padding: '14px',
                                        textAlign: 'center',
                                        color: tebox.colors.textSecondary,
                                        fontFamily: bookingBrandFontFamilyVar,
                                        fontSize:
                                          teboxDesignSystem.typography.caption.fontSize,
                                      }}
                                    >
                                      Searching…
                                    </div>
                                  ) : playerSearchResults[playerNumber - 2] &&
                                    playerSearchResults[playerNumber - 2].length > 0 ? (
                                    playerSearchResults[playerNumber - 2].map((member: any) => (
                                      <div
                                        key={member.id}
                                        onClick={() =>
                                          handleMemberSelectFromDropdown(playerNumber - 2, member)
                                        }
                                        style={{
                                          padding: '11px 14px',
                                          cursor: 'pointer',
                                          borderBottom: `1px solid ${tebox.colors.border}`,
                                          fontFamily: bookingBrandFontFamilyVar,
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor =
                                            tebox.colors.bgSecondary;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = '#FFFFFF';
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontWeight: 500,
                                            color: brandColors.textColor,
                                            fontSize:
                                              teboxDesignSystem.typography.body.fontSize,
                                          }}
                                        >
                                          {member.firstName} {member.lastName}
                                        </div>
                                        {member.membershipNumber && (
                                          <div
                                            style={{
                                              fontSize:
                                                teboxDesignSystem.typography.caption.fontSize,
                                              color: tebox.colors.textSecondary,
                                              marginTop: '2px',
                                            }}
                                          >
                                            #{member.membershipNumber}
                                          </div>
                                        )}
                                      </div>
                                    ))
                                  ) : playerSearchQueries[playerNumber - 2] &&
                                    playerSearchQueries[playerNumber - 2].length >= 1 ? (
                                    <div
                                      style={{
                                        padding: '14px',
                                        textAlign: 'center',
                                        color: tebox.colors.textSecondary,
                                        fontFamily: bookingBrandFontFamilyVar,
                                        fontSize:
                                          teboxDesignSystem.typography.caption.fontSize,
                                      }}
                                    >
                                      No members match
                                    </div>
                                  ) : null}
                                </div>
                              )}
                          </div>
                        );

                        if (isMobile) {
                          return (
                            <div
                              key={`player-${playerNumber}`}
                              role="group"
                              aria-label={`Player ${playerNumber}`}
                              style={{
                                padding: '14px 0',
                                borderBottom: isLast
                                  ? undefined
                                  : `1px solid ${partnerRowDivider}`,
                              }}
                            >
                              {selectedMember ? (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                    width: '100%',
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                      gap: '10px',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontFamily: bookingBrandFontFamilyVar,
                                        fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                                        fontWeight: 500,
                                        color: tebox.colors.textSecondary,
                                      }}
                                    >
                                      Player {playerNumber}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveMember(playerNumber)}
                                      style={{
                                        fontFamily: bookingBrandFontFamilyVar,
                                        fontSize:
                                          teboxDesignSystem.typography.caption.fontSize,
                                        fontWeight: 500,
                                        background: 'none',
                                        border: 'none',
                                        padding: '4px 0',
                                        color: tebox.colors.textSecondary,
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        textUnderlineOffset: '2px',
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <span
                                    style={{
                                      fontFamily: bookingBrandFontFamilyVar,
                                      fontWeight: 500,
                                      fontSize: teboxDesignSystem.typography.body.fontSize,
                                      color: brandColors.textColor,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      minWidth: 0,
                                      flex: '1 1 auto',
                                      textAlign: 'right',
                                    }}
                                  >
                                    {selectedMember.member.firstName}{' '}
                                    {selectedMember.member.lastName}
                                  </span>
                                </div>
                              ) : (
                                <>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                      gap: '10px',
                                      marginBottom: '10px',
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontFamily: bookingBrandFontFamilyVar,
                                        fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                                        fontWeight: 500,
                                        color: tebox.colors.textSecondary,
                                      }}
                                    >
                                      Player {playerNumber}
                                    </div>
                                  </div>
                                  <div>{searchField}</div>
                                </>
                              )}
                            </div>
                          );
                        }

                        return (
                          <div
                            key={`player-${playerNumber}`}
                            role="row"
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'minmax(0, max-content) minmax(0, 1fr)',
                              columnGap: '16px',
                              alignItems: 'center',
                              padding: '14px 0',
                              borderBottom: isLast
                                ? undefined
                                : `1px solid ${partnerRowDivider}`,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px',
                                minWidth: 0,
                              }}
                            >
                              <div
                                style={{
                                  fontFamily: bookingBrandFontFamilyVar,
                                  fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                                  fontWeight: 500,
                                  color: tebox.colors.textSecondary,
                                }}
                              >
                                Player {playerNumber}
                              </div>
                              {selectedMember ? (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveMember(playerNumber)}
                                  style={{
                                    fontFamily: bookingBrandFontFamilyVar,
                                    fontSize:
                                      teboxDesignSystem.typography.caption.fontSize,
                                    fontWeight: 500,
                                    background: 'none',
                                    border: 'none',
                                    padding: '4px 0',
                                    flexShrink: 0,
                                    color: tebox.colors.textSecondary,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '2px',
                                  }}
                                >
                                  Remove
                                </button>
                              ) : null}
                            </div>
                            <div style={{ minWidth: 0, width: '100%' }}>
                              {selectedMember ? (
                                <span
                                  style={{
                                    fontFamily: bookingBrandFontFamilyVar,
                                    fontWeight: 500,
                                    fontSize: teboxDesignSystem.typography.body.fontSize,
                                    color: brandColors.textColor,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                    minWidth: 0,
                                    width: '100%',
                                    textAlign: 'right',
                                  }}
                                >
                                  {selectedMember.member.firstName}{' '}
                                  {selectedMember.member.lastName}
                                </span>
                              ) : (
                                searchField
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Cart Options — heading + cart row in one tinted panel */}
            <div className="space-y-3">
              <div className={checkoutHighlightPanelClass}>
                {!isMobile && (
                  <div className="flex w-full min-w-0 items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <h2 style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontStyle: 'normal',
                        fontWeight: '500',
                        fontSize: isMobile ? '18px' : '20px',
                        lineHeight: isMobile ? '22px' : '21px',
                        color: brandColors.textColor,
                        margin: 0
                      }}>Cart Options</h2>
                      <p style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontStyle: 'normal',
                        fontWeight: '400',
                        fontSize: isMobile ? '13px' : '14px',
                        color: brandColors.secondaryColor,
                        margin: '4px 0 0 0'
                      }}>{isMandatoryCartsForCheckout
                        ? 'A golf cart is included for every player on this course.'
                        : 'Select which players need a golf cart'}</p>
                    </div>
                    <div className="flex flex-shrink-0 flex-col items-end gap-1">
                      <h2 style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontStyle: 'normal',
                        fontWeight: '500',
                        fontSize: isMobile ? '18px' : '20px',
                        lineHeight: isMobile ? '22px' : '21px',
                        color: brandColors.textColor,
                        margin: 0,
                      }}>Players</h2>
                      <div className="flex items-center justify-end">
                        <div style={{ width: '25.6px', marginRight: '25.6px' }} aria-hidden />
                        {Array.from({ length: searchPlayers }, (_, index) => {
                          const playerNumber = index + 1;
                          const initials = bookingCheckoutPlayerInitials(
                            playerNumber,
                            authenticatedCustomer,
                            selectedMembers
                          );
                          return (
                            <div
                              key={`header-${index}`}
                              role="columnheader"
                              aria-label={bookingCheckoutPlayerFullLabel(
                                playerNumber,
                                authenticatedCustomer,
                                selectedMembers
                              )}
                              style={{
                                fontFamily: bookingBrandFontFamilyVar,
                                fontStyle: 'normal',
                                fontWeight: 500,
                                fontSize: teboxDesignSystem.typography.caption.fontSize,
                                lineHeight: '1.1',
                                letterSpacing: '-0.02em',
                                color: brandColors.textColor,
                                width: '25.6px',
                                height: '25.6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: index < searchPlayers - 1 ? '25.6px' : '0'
                              }}
                            >
                              {initials}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                {isMobile && (
                  <div>
                    <h2 style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: isMobile ? '18px' : '20px',
                      lineHeight: isMobile ? '22px' : '21px',
                      color: brandColors.textColor,
                      margin: 0
                    }}>Cart Options</h2>
                    <p style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: isMobile ? '13px' : '14px',
                      color: brandColors.secondaryColor,
                      margin: '4px 0 0 0'
                    }}>{isMandatoryCartsForCheckout
                      ? 'A golf cart is included for every player on this course.'
                      : 'Select which players need a golf cart'}</p>
                  </div>
                )}
                {/* Cart Selection */}
                <div className={isMobile ? 'py-2.5' : 'py-3'}>
                  <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-between'}`}>
                    <div className={`flex items-center ${isMobile ? 'gap-2' : 'gap-3'}`}>
                      <span style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontStyle: 'normal',
                        fontWeight: '500',
                        fontSize: isMobile ? '13px' : '14px',
                        color: brandColors.textColor
                      }}>{(() => {
                        const is18Hole = selectedTeeTime.holeSet === '18-hole' || selectedTeeTime.holeSet === '18';
                        const holeCount = is18Hole ? 18 : 9;
                        
                        // Use dynamic cart product selection
                        const cartProducts = allProducts.filter(p => p.category === 'cart-rental');
                        const productsForHoles = cartProducts.filter(p => {
                          const name = p.name.toUpperCase();
                          const is18Hole = name.includes('18 HOLES') || name.includes('18-HOLE');
                          const is9Hole = name.includes('9 HOLES') || name.includes('9-HOLE');
                          
                          if (holeCount === 18) return is18Hole;
                          if (holeCount === 9) return is9Hole;
                          return false;
                        });
                        
                        // Fallback priority: half > full > 1-person
                        const fallbackOrder = ['HALF', 'FULL', '1-PERSON'];
                        let cartProduct = null;
                        
                        for (const type of fallbackOrder) {
                          cartProduct = productsForHoles.find(p => {
                            const productType = p.name.toUpperCase().includes('HALF') ? 'HALF' : 
                                              p.name.toUpperCase().includes('FULL') ? 'FULL' : 
                                              p.name.toUpperCase().includes('1 PERSON') ? '1-PERSON' : 'UNKNOWN';
                            return productType === type;
                          });
                          
                          if (cartProduct) break;
                        }
                        
                        const cartPrice = cartProduct?.price || 0;
                        return `${cartProduct?.name || `Golf Cart - ${holeCount} Holes`} - $${cartPrice.toFixed(2)}`;
                      })()}</span>
                    </div>
                    
                    {/* Mobile: Dropdown, Desktop: Checkboxes — mandatory carts: all players, no removal */}
                    {isMobile ? (
                      isMandatoryCartsForCheckout ? (
                        <div
                          className="w-full px-4 py-3 border border-tebox-border rounded-[6px] bg-white"
                          style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontSize: '14px',
                            minHeight: '44px',
                            display: 'flex',
                            alignItems: 'center',
                            color: brandColors.textColor,
                          }}
                          aria-readonly="true"
                        >
                          All players — cart included
                        </div>
                      ) : (
                      <div style={{ position: 'relative' }} data-cart-dropdown>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenCartDropdown(!openCartDropdown);
                          }}
                          className={`w-full ${isMobile ? 'px-4 py-3' : 'px-3 py-2'} border border-tebox-border rounded-[6px] focus:outline-none focus:border-black flex items-center justify-between`}
                          style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontSize: isMobile ? '14px' : '13px',
                            minHeight: isMobile ? '44px' : '36px',
                            backgroundColor: '#ffffff'
                          }}
                        >
                          <span>
                            {cartAssignments.length === 0 
                              ? 'Select players' 
                              : cartAssignments.length === 1 
                                ? bookingCheckoutPlayerInitials(
                                    cartAssignments[0] + 1,
                                    authenticatedCustomer,
                                    selectedMembers
                                  )
                                : `${cartAssignments.length} players selected`}
                          </span>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: openCartDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                            <path d="M4 6L8 10L12 6" stroke={tebox.colors.textSecondary} strokeWidth={ICON_STROKE.standard} strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        
                        {openCartDropdown && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: '#FFFFFF',
                            border: `1px solid ${tebox.colors.border}`,
                            borderRadius: 'var(--brand-border-radius, 6px)',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                            zIndex: 1000,
                            marginTop: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }}>
                            {Array.from({ length: searchPlayers }, (_, index) => {
                              const playerNumber = index + 1;
                              const isAssigned = cartAssignments.includes(index);
                              const selectedMember = selectedMembers.find(sm => sm.playerNumber === playerNumber);
                              
                              return (
                                <div
                                  key={`cart-player-${index}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePlayerToggle("cart", index);
                                  }}
                                  style={{
                                    padding: '12px',
                                    cursor: 'pointer',
                                    borderBottom: `1px solid ${tebox.colors.border}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    backgroundColor: isAssigned ? tebox.colors.bgSecondary : tebox.colors.bgPrimary
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = tebox.colors.bgSecondary;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = isAssigned ? tebox.colors.bgSecondary : tebox.colors.bgPrimary;
                                  }}
                                >
                                  <div style={{
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}>
                                    <CheckoutPlayerAssignmentToggle
                                      size={24}
                                      isAssigned={isAssigned}
                                      brandColors={brandColors}
                                      borderColor={tebox.colors.border}
                                      borderWidth={2}
                                    />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{
                                      fontFamily: bookingBrandFontFamilyVar,
                                      fontSize: teboxDesignSystem.typography.body.fontSize,
                                      fontWeight: '500',
                                      color: brandColors.textColor
                                    }}>
                                      {playerNumber === 1 
                                        ? (authenticatedCustomer ? `${authenticatedCustomer.firstName} ${authenticatedCustomer.lastName}` : 'Player 1')
                                        : selectedMember 
                                          ? `${selectedMember.member.firstName} ${selectedMember.member.lastName}`
                                          : `Player ${playerNumber}`}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      )
                    ) : (
                      /* Desktop: Checkbox layout */
                      <div className="flex items-center">
                        <div style={{ width: '25.6px', marginRight: '25.6px' }}></div>
                        {Array.from({ length: searchPlayers }, (_, index) => {
                          const isAssigned = cartAssignments.includes(index);
                          return (
                            <div
                              key={`circle-container-${index}`}
                              style={{
                                width: '25.6px',
                                height: '25.6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: index < searchPlayers - 1 ? '25.6px' : '0'
                              }}
                            >
                              <div
                                onClick={isMandatoryCartsForCheckout ? undefined : () => handlePlayerToggle("cart", index)}
                                className={isMandatoryCartsForCheckout ? undefined : 'cursor-pointer'}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: isMandatoryCartsForCheckout ? 'default' : undefined,
                                }}
                                title={
                                  isMandatoryCartsForCheckout
                                    ? `Cart included for ${bookingCheckoutPlayerFullLabel(index + 1, authenticatedCustomer, selectedMembers)}`
                                    : `Click to ${isAssigned ? 'remove' : 'add'} cart for ${bookingCheckoutPlayerFullLabel(index + 1, authenticatedCustomer, selectedMembers)}`
                                }
                                role={isMandatoryCartsForCheckout ? 'presentation' : undefined}
                              >
                                <CheckoutPlayerAssignmentToggle
                                  size={25.6}
                                  isAssigned={isMandatoryCartsForCheckout || isAssigned}
                                  brandColors={brandColors}
                                  borderColor={tebox.colors.border}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Extras */}
            <div className="space-y-3">
              <div className={checkoutHighlightPanelClass}>
                <div>
                  <h2 style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontSize: isMobile ? '18px' : '20px',
                    lineHeight: isMobile ? '22px' : '21px',
                    color: brandColors.textColor,
                    margin: 0
                  }}>Extras</h2>
                  <p style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontStyle: 'normal',
                    fontWeight: '400',
                    fontSize: isMobile ? '13px' : '14px',
                    color: brandColors.secondaryColor,
                    margin: '4px 0 0 0'
                  }}>Add additional services for your round</p>
                </div>
                {Array.isArray(extras) && extras.length > 0 ? (
                  extras.map((extra, extraIndex) => {
                    const assignments = extrasAssignments[extra.id] || [];
                    const isOpen = openExtrasDropdowns[extra.id] || false;
                    const showDividerBelow = extraIndex < extras.length - 1;
                    
                    return (
                      <div
                        key={extra.id}
                        className={isMobile ? 'py-2.5' : 'py-3'}
                        style={showDividerBelow ? checkoutItemDividerStyle : undefined}
                      >
                        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-between'}`}>
                          <div className={`flex items-center ${isMobile ? 'gap-2' : 'gap-3'}`}>
                            <span style={{
                              fontFamily: bookingBrandFontFamilyVar,
                              fontStyle: 'normal',
                              fontWeight: '500',
                              fontSize: isMobile ? '13px' : '14px',
                              color: brandColors.textColor
                            }}>{extra.name} - ${extra.price || 0}</span>
                          </div>
                          
                          {/* Mobile: Dropdown, Desktop: Checkboxes */}
                          {isMobile ? (
                            <div style={{ position: 'relative', width: '100%' }} data-extra-dropdown={extra.id}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenExtrasDropdowns(prev => ({
                                    ...prev,
                                    [extra.id]: !isOpen
                                  }));
                                }}
                                className={`w-full ${isMobile ? 'px-4 py-3' : 'px-3 py-2'} border border-tebox-border rounded-[6px] focus:outline-none focus:border-black flex items-center justify-between`}
                                style={{
                                  fontFamily: bookingBrandFontFamilyVar,
                                  fontSize: isMobile ? '14px' : '13px',
                                  minHeight: isMobile ? '44px' : '36px',
                                  backgroundColor: '#ffffff'
                                }}
                              >
                                <span>
                                  {assignments.length === 0 
                                    ? 'Select players' 
                                    : assignments.length === 1 
                                      ? bookingCheckoutPlayerInitials(
                                          assignments[0] + 1,
                                          authenticatedCustomer,
                                          selectedMembers
                                        )
                                      : `${assignments.length} players selected`}
                                </span>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                                  <path d="M4 6L8 10L12 6" stroke={tebox.colors.textSecondary} strokeWidth={ICON_STROKE.standard} strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              
                              {isOpen && (
                                <div style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  backgroundColor: '#FFFFFF',
                                  border: `1px solid ${tebox.colors.border}`,
                                  borderRadius: 'var(--brand-border-radius, 6px)',
                                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                  zIndex: 1000,
                                  marginTop: '4px',
                                  maxHeight: '200px',
                                  overflowY: 'auto'
                                }}>
                                  {Array.from({ length: searchPlayers }, (_, index) => {
                                    const playerNumber = index + 1;
                                    const isAssigned = assignments.includes(index);
                                    const selectedMember = selectedMembers.find(sm => sm.playerNumber === playerNumber);
                                    
                                    return (
                                      <div
                                        key={`extra-player-${extra.id}-${index}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePlayerToggle(extra.id, index);
                                        }}
                                        style={{
                                          padding: '12px',
                                          cursor: 'pointer',
                                          borderBottom: `1px solid ${tebox.colors.border}`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '10px',
                                          backgroundColor: isAssigned ? tebox.colors.bgSecondary : tebox.colors.bgPrimary
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = tebox.colors.bgSecondary;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = isAssigned ? tebox.colors.bgSecondary : tebox.colors.bgPrimary;
                                        }}
                                      >
                                        <div style={{
                                          width: '24px',
                                          height: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0,
                                        }}>
                                          <CheckoutPlayerAssignmentToggle
                                            size={24}
                                            isAssigned={isAssigned}
                                            brandColors={brandColors}
                                            borderColor={tebox.colors.border}
                                            borderWidth={2}
                                          />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                          <div style={{
                                            fontFamily: bookingBrandFontFamilyVar,
                                            fontSize: teboxDesignSystem.typography.body.fontSize,
                                            fontWeight: '500',
                                            color: brandColors.textColor
                                          }}>
                                            {playerNumber === 1 
                                              ? (authenticatedCustomer ? `${authenticatedCustomer.firstName} ${authenticatedCustomer.lastName}` : 'Player 1')
                                              : selectedMember 
                                                ? `${selectedMember.member.firstName} ${selectedMember.member.lastName}`
                                                : `Player ${playerNumber}`}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ) : (
                            /* Desktop: Checkbox layout */
                            <div className="flex items-center">
                              <div style={{ width: '25.6px', marginRight: '25.6px' }}></div>
                              {Array.from({ length: searchPlayers }, (_, index) => {
                                const isAssigned = assignments.includes(index);
                                return (
                                  <div
                                    key={`circle-container-${index}`}
                                    style={{
                                      width: '25.6px',
                                      height: '25.6px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginRight: index < searchPlayers - 1 ? '25.6px' : '0'
                                    }}
                                  >
                                    <div
                                      onClick={() => handlePlayerToggle(extra.id, index)}
                                      className="cursor-pointer"
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                      title={`Click to ${isAssigned ? 'remove' : 'add'} ${extra.name} for ${bookingCheckoutPlayerFullLabel(index + 1, authenticatedCustomer, selectedMembers)}`}
                                    >
                                      <CheckoutPlayerAssignmentToggle
                                        size={25.6}
                                        isAssigned={isAssigned}
                                        brandColors={brandColors}
                                        borderColor={tebox.colors.border}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontStyle: 'normal',
                    fontWeight: '400',
                    fontSize: teboxDesignSystem.typography.body.fontSize,
                    color: brandColors.secondaryColor
                  }} className="text-center py-4">
                    No extras available
                  </div>
                )}
              </div>
            </div>

            </div>
          </div>

          <div
            className={`min-w-0 ${isMobile ? 'w-full' : 'shrink-0'} bg-white rounded-[6px] overflow-hidden`}
            style={{
              width: isMobile ? '100%' : 576,
              ...(isMobile
                ? { overflow: 'visible', boxSizing: 'border-box' }
                : {
                    maxHeight: 'calc(100vh - 160px)',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    touchAction: 'pan-y',
                    boxSizing: 'border-box',
                  }),
            }}
          >
            <div
              style={{
                padding: isMobile ? '16px' : '24px',
                boxSizing: 'border-box',
              }}
              className={isMobile ? 'space-y-4' : 'space-y-6'}
            >
            {/* Summary Section — heading + line items (same brand chrome as pricing modal notes) */}
            <div
              className={isMobile ? 'space-y-2' : 'space-y-3'}
              style={{
                padding: '16px',
                borderRadius: 'var(--brand-border-radius, 6px)',
                boxSizing: 'border-box',
                ...reservationSummaryBrandChrome,
              }}
            >
              <div className={`min-w-0 ${isMobile ? 'space-y-1.5' : 'space-y-2'}`}>
                <div className="flex min-w-0 items-center gap-2">
                  <BookingsIcon
                    size={isMobile ? 20 : 22}
                    color={brandColors.textColor}
                    strokeWidth={ICON_STROKE.standard}
                  />
                  <h2
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: isMobile ? '18px' : '20px',
                      lineHeight: isMobile ? '22px' : '21px',
                      color: '#000000',
                      margin: 0,
                      flexShrink: 0,
                    }}
                  >
                    Reservation Summary
                  </h2>
                </div>
                <div className="min-w-0 text-left" title={`${selectedTeeTime.courseName}`}>
                  <p
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: teboxDesignSystem.typography.caption.fontSize,
                      lineHeight: 1.35,
                      color: tebox.colors.textSecondary,
                      margin: 0,
                    }}
                  >
                    {selectedTeeTime.dateTime.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                    {' · '}
                    {selectedTeeTime.dateTime.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                    {' · '}
                    {searchPlayers} {searchPlayers === 1 ? 'Player' : 'Players'}
                  </p>
                  <p
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: teboxDesignSystem.typography.caption.fontSize,
                      lineHeight: 1.35,
                      color: brandColors.textColor,
                      margin: '2px 0 0 0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedTeeTime.courseName}
                  </p>
                </div>
              </div>

                {/* Green Fee - Grouped, with pass-applied split (per-player rates when set) */}
                {(() => {
                  const is18Hole = selectedTeeTime.holeSet === '18-hole' || selectedTeeTime.holeSet === '18';
                  const holeCount = is18Hole ? 18 : 9;
                  const golfRoundProduct = allProducts.find(p => {
                    const isGolfRound = p.category === 'golf-round';
                    const nameMatches = 
                      p.name === `${holeCount} Hole - Green Fee` ||
                      p.name === `ROUND OF GOLF - ${holeCount} HOLES` ||
                      (p.name.toLowerCase().includes(`${holeCount} hole`) && p.name.toLowerCase().includes('green fee')) ||
                      (p.name.toLowerCase().includes(`${holeCount} hole`) && p.name.toLowerCase().includes('golf'));
                    return isGolfRound && nameMatches;
                  });
                  const label = golfRoundProduct?.name || `${holeCount} Hole - Green Fee`;
                  
                  let coversGreenFee = false;
                  if (appliedPass && golfRoundProduct) {
                    const includedProducts = appliedPass.passProduct?.includedProducts || [];
                    if (Array.isArray(includedProducts) && includedProducts.length > 0) {
                      coversGreenFee = passCatalogProductMatchesInclusion(includedProducts, golfRoundProduct);
                    }
                  }
                  
                  const useMix =
                    checkoutPerPlayerGreenFees.length === searchPlayers && searchPlayers > 0;
                  let billedGreen = 0;
                  let bookerGreenStrikethrough = selectedTeeTime.price || 0;
                  let fullPricePlayerCount = 0;

                  if (useMix) {
                    bookerGreenStrikethrough = Number(checkoutPerPlayerGreenFees[0]) || 0;
                    for (let i = 0; i < searchPlayers; i++) {
                      if (coversGreenFee && i === 0) continue;
                      billedGreen += Number(checkoutPerPlayerGreenFees[i]) || 0;
                      fullPricePlayerCount += 1;
                    }
                  } else {
                    const coveredByPass = coversGreenFee ? 1 : 0;
                    fullPricePlayerCount = Math.max(0, searchPlayers - coveredByPass);
                    const perPlayerPrice = selectedTeeTime.price || 0;
                    billedGreen = perPlayerPrice * fullPricePlayerCount;
                    bookerGreenStrikethrough = perPlayerPrice;
                  }

                  return (
                    <>
                      {billedGreen > 0 && (
                        <div className="flex justify-between text-tebox-body">
                          <span style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: '400',
                            fontSize: isMobile ? '13px' : '14px',
                            color: tebox.colors.textSecondary
                          }}>
                            {label}{useMix ? ' (by player rate)' : ` ×${fullPricePlayerCount}`}
                          </span>
                          <span style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: '500',
                            fontSize: isMobile ? '13px' : '14px',
                            color: '#000000'
                          }}>
                            ${billedGreen.toFixed(2)}
                          </span>
                        </div>
                      )}

                      {coversGreenFee && (
                        <div className="flex justify-between text-tebox-body">
                          <span style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: '400',
                            fontSize: isMobile ? '13px' : '14px',
                            color: tebox.colors.textSecondary
                          }}>
                            {label} ×1 (Pass applied)
                          </span>
                          <span style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: '500',
                            fontSize: isMobile ? '13px' : '14px',
                            color: '#000000',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end'
                          }}>
                            <span style={{
                              textDecorationLine: 'line-through',
                              color: tebox.colors.textTertiary,
                              fontSize: isMobile ? '12px' : '13px'
                            }}>
                              ${bookerGreenStrikethrough.toFixed(2)}
                            </span>
                            <span style={{
                              color: reservationSummaryAdjustedPriceColor,
                              fontSize: isMobile ? '13px' : '14px'
                            }}>
                              ${(0).toFixed(2)}
                            </span>
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Cart Item - Show player assignments */}
                {cartAssignments.length > 0 && (
                  <div className="flex justify-between text-tebox-body">
                    <div className="flex items-center gap-2">
                      <span style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontStyle: 'normal',
                        fontWeight: '400',
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        color: tebox.colors.textSecondary
                      }}>
                        {(() => {
                          const is18Hole = selectedTeeTime.holeSet === '18-hole' || selectedTeeTime.holeSet === '18';
                          const holeCount = is18Hole ? 18 : 9;
                          // Use dynamic cart product selection
                          const cartProducts = allProducts.filter(p => p.category === 'cart-rental');
                          const productsForHoles = cartProducts.filter(p => {
                            const name = p.name.toUpperCase();
                            const is18 = name.includes('18 HOLES') || name.includes('18-HOLE');
                            const is9 = name.includes('9 HOLES') || name.includes('9-HOLE');
                            
                            if (holeCount === 18) return is18;
                            if (holeCount === 9) return is9;
                            return false;
                          });
                          
                          // Fallback priority: half > full > 1-person
                          const fallbackOrder = ['HALF', 'FULL', '1-PERSON'];
                          let cartProduct: any = null;
                          
                          for (const type of fallbackOrder) {
                            cartProduct = productsForHoles.find((p: any) => {
                              const upper = p.name.toUpperCase();
                              const productType = upper.includes('HALF')
                                ? 'HALF'
                                : upper.includes('FULL')
                                ? 'FULL'
                                : upper.includes('1 PERSON')
                                ? '1-PERSON'
                                : 'UNKNOWN';
                              return productType === type;
                            });
                            
                            if (cartProduct) break;
                          }
                          return `${cartProduct?.name || `Golf Cart - ${holeCount} Holes`} (Players ${cartAssignments.map(p => p + 1).join(', ')})`;
                        })()}
                      </span>
                    </div>
                    <span style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      color: '#000000'
                    }}>
                      {(() => {
                        const is18Hole = selectedTeeTime.holeSet === '18-hole' || selectedTeeTime.holeSet === '18';
                        const holeCount = is18Hole ? 18 : 9;
                        
                        // Use dynamic cart product selection
                        const cartProducts = allProducts.filter(p => p.category === 'cart-rental');
                        const productsForHoles = cartProducts.filter(p => {
                          const name = p.name.toUpperCase();
                          const is18 = name.includes('18 HOLES') || name.includes('18-HOLE');
                          const is9 = name.includes('9 HOLES') || name.includes('9-HOLE');
                          
                          if (holeCount === 18) return is18;
                          if (holeCount === 9) return is9;
                          return false;
                        });
                        
                        const fallbackOrder = ['HALF', 'FULL', '1-PERSON'];
                        let cartProduct: any = null;
                        
                        for (const type of fallbackOrder) {
                          cartProduct = productsForHoles.find((p: any) => {
                            const upper = p.name.toUpperCase();
                            const productType = upper.includes('HALF')
                              ? 'HALF'
                              : upper.includes('FULL')
                              ? 'FULL'
                              : upper.includes('1 PERSON')
                              ? '1-PERSON'
                              : 'UNKNOWN';
                            return productType === type;
                          });
                          if (cartProduct) break;
                        }

                        const includedForPass = appliedPass?.passProduct?.includedProducts || [];
                        let coversCart = false;
                        if (appliedPass && cartProduct) {
                          if (Array.isArray(includedForPass) && includedForPass.length > 0) {
                            coversCart = passCatalogProductMatchesInclusion(includedForPass, cartProduct);
                          }
                        }
                        const cartPrice =
                          coversCart && cartProduct
                            ? passDiscountUnitPrice(includedForPass, cartProduct)
                            : (cartProduct?.price || 0);
                        
                        const coveredCarts = coversCart ? 1 : 0;
                        const chargedCarts = Math.max(
                          0,
                          cartAssignments.length - coveredCarts
                        );

                        if (!coversCart) {
                          return `$${(cartAssignments.length * cartPrice).toFixed(2)}`;
                        }

                        return (
                          <span
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                            }}
                          >
                            <span
                              style={{
                                textDecorationLine: 'line-through',
                                color: tebox.colors.textTertiary,
                                fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                              }}
                            >
                              ${(cartAssignments.length * cartPrice).toFixed(2)}
                            </span>
                            <span
                              style={{
                                color: reservationSummaryAdjustedPriceColor,
                                fontSize: teboxDesignSystem.typography.body.fontSize,
                              }}
                            >
                              ${(chargedCarts * cartPrice).toFixed(2)}
                            </span>
                          </span>
                        );
                      })()}
                    </span>
                  </div>
                )}





                {/* Extras Items */}
                {Array.isArray(extras) && extras.map((extra) => {
                  const assignments = extrasAssignments[extra.id] || [];
                  if (assignments.length > 0) {
                    return (
                      <div key={extra.id} className="flex justify-between text-tebox-body">
                        <span style={{
                          fontFamily: bookingBrandFontFamilyVar,
                          fontStyle: 'normal',
                          fontWeight: '400',
                          fontSize: isMobile ? '13px' : '14px',
                          color: brandColors.secondaryColor
                        }}>
                          {extra.name} (Players {assignments.map(p => p + 1).join(', ')})
                        </span>
                        <span style={{
                          fontFamily: bookingBrandFontFamilyVar,
                          fontStyle: 'normal',
                          fontWeight: '500',
                          fontSize: isMobile ? '13px' : '14px',
                          color: brandColors.textColor
                        }}>${((extra.price || 0) * assignments.length).toFixed(2)}</span>
                      </div>
                    );
                  }
                  return null;
                })}

                {/* Sub-total */}
                <div className="pt-3">
                  <div className="flex justify-between text-tebox-body font-medium">
                    <span style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: isMobile ? '15px' : '16px',
                      color: '#000000'
                    }}>Sub-total:</span>
                    <span style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: isMobile ? '15px' : '16px',
                      color: '#000000'
                    }}>
                      ${taxCalculation
                        ? taxCalculation.subtotal.toFixed(2)
                        : (checkoutPricing?.fallbackSubtotalBeforeCoupon ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Coupon Discount */}
                {appliedCoupon && couponDiscount > 0 && (
                  <div className="flex justify-between text-tebox-body">
                    <span style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: isMobile ? '13px' : '14px',
                      color: reservationSummaryAdjustedPriceColor
                    }}>
                      Discount ({appliedCoupon.code}):
                    </span>
                    <span style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: isMobile ? '13px' : '14px',
                      color: reservationSummaryAdjustedPriceColor
                    }}>
                      -${couponDiscount.toFixed(2)}
                    </span>
                  </div>
                )}

                {/* Tax */}
                {taxCalculation && taxCalculation.taxAmount > 0 && (
                  <div className="flex justify-between text-tebox-body">
                    <span style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: isMobile ? '13px' : '14px',
                      color: brandColors.secondaryColor
                    }}>
                      Tax {taxCalculation.taxBreakdown.length > 0 ? `(${taxCalculation.taxBreakdown[0].taxClassName})` : ''}:
                    </span>
                    <span style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: isMobile ? '13px' : '14px',
                      color: '#000000'
                    }}>
                      ${taxCalculation.taxAmount.toFixed(2)}
                    </span>
                  </div>
                )}

                {/* Total */}
                <div className="pt-3">
                  <div className="flex justify-between text-tebox-h4 font-medium">
                    <span style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: isMobile ? '18px' : '20px',
                      color: brandColors.textColor
                    }}>Total:</span>
                    <span style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: isMobile ? '18px' : '20px',
                      color: brandColors.textColor
                    }}>
                      ${(() => {
                        const fallbackNet = checkoutPricing?.fallbackSubtotalBeforeCoupon ?? 0;
                        const baseTotal = taxCalculation ? taxCalculation.total : fallbackNet;
                        return Math.max(0, baseTotal - couponDiscount).toFixed(2);
                      })()}
                    </span>
                  </div>
                </div>
            </div>

            {/* Passes — own section (card); layout matches Coupons (input row + green applied card) */}
            <section
              className={`rounded-[6px] ${isMobile ? 'space-y-2 p-4' : 'space-y-3 p-5'}`}
              style={{
                ...checkoutAuxiliarySectionSurfaceStyle,
                border: reservationSummaryBrandChrome.border,
              }}
              aria-labelledby="checkout-passes-heading"
            >
              <div className="flex items-center gap-2">
                <PassesIcon
                  size={isMobile ? 20 : 22}
                  color={brandColors.textColor}
                  strokeWidth={ICON_STROKE.standard}
                />
                <h3
                  id="checkout-passes-heading"
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontSize: isMobile ? '15px' : '16px',
                    lineHeight: isMobile ? '22px' : '24px',
                    color: brandColors.textColor,
                    margin: 0
                  }}
                >
                  Passes
                </h3>
              </div>
              <div>
                {!authenticatedCustomer ? (
                  <div className="text-center">
                    <div style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      color: teboxDesignSystem.colors.text.secondary,
                      marginBottom: '12px'
                    }}>
                      Login to apply your pass
                    </div>
                    <button
                      onClick={() => setShowLoginModal(true)}
                      className="px-4 py-2 text-tebox-body font-medium text-white bg-blue-600 border border-blue-600 rounded-[6px] hover:bg-blue-700 transition-colors"
                      style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontWeight: '500',
                        fontSize: teboxDesignSystem.typography.body.fontSize
                      }}
                    >
                      Login
                    </button>
                  </div>
                ) : (
                  <div className={isMobile ? 'space-y-2' : 'space-y-3'}>
                    {availablePasses.length > 0 && (
                      <div className={`flex ${isMobile ? 'flex-col' : ''} ${isMobile ? 'gap-2' : 'gap-3'}`}>
                        <div className="relative min-w-0 flex-1">
                          <button
                            type="button"
                            aria-haspopup="listbox"
                            aria-expanded={showPassDropdown}
                            onClick={() => {
                              setShowCouponDropdown(false);
                              setShowPassDropdown(!showPassDropdown);
                            }}
                            className={`w-full ${isMobile ? 'px-4 py-3.5 text-tebox-body' : 'px-4 py-3 text-tebox-body'} border border-tebox-border rounded-[6px] text-left focus:outline-none focus:border-black bg-white`}
                            style={{
                              fontFamily: bookingBrandFontFamilyVar,
                              fontStyle: 'normal',
                              fontWeight: '400',
                              fontSize: isMobile ? '15px' : '14px',
                              color: brandColors.textColor,
                              minHeight: isMobile ? '44px' : 'auto'
                            }}
                          >
                            {showPassDropdown
                              ? 'Hide available passes'
                              : selectedPass
                                ? (selectedPass.passProduct?.name || 'Selected pass')
                                : `View ${availablePasses.length} available passes`}
                          </button>
                          {showPassDropdown && (
                            <div
                              className="absolute top-full left-0 right-0 z-10 mt-1 max-h-48 overflow-y-auto divide-y divide-tebox-border rounded-[6px] border border-tebox-border bg-white shadow-lg"
                              role="listbox"
                            >
                              <button
                                type="button"
                                role="option"
                                onClick={() => {
                                  handlePassSelect('none');
                                  setShowPassDropdown(false);
                                }}
                                className={`w-full ${isMobile ? 'px-4 py-3.5' : 'px-4 py-3'} text-left hover:bg-tebox-hover`}
                                style={{
                                  fontFamily: bookingBrandFontFamilyVar,
                                  fontStyle: 'normal',
                                  fontWeight: '400',
                                  fontSize: isMobile ? '15px' : '14px',
                                  minHeight: isMobile ? '44px' : 'auto'
                                }}
                              >
                                <div className={`${isMobile ? 'text-tebox-body' : 'text-tebox-body'} text-tebox-muted`} style={{ fontSize: isMobile ? '15px' : '14px' }}>No pass selected</div>
                              </button>
                              {availablePasses.map((pass: any) => (
                                <button
                                  type="button"
                                  role="option"
                                  key={pass.id}
                                  aria-selected={selectedPass?.id === pass.id}
                                  onClick={() => {
                                    handlePassSelect(pass.id);
                                    setShowPassDropdown(false);
                                  }}
                                  className={`w-full ${isMobile ? 'px-4 py-3.5' : 'px-4 py-3'} text-left hover:bg-tebox-hover`}
                                  style={{
                                    fontFamily: bookingBrandFontFamilyVar,
                                    fontStyle: 'normal',
                                    fontWeight: '400',
                                    fontSize: isMobile ? '15px' : '14px',
                                    minHeight: isMobile ? '44px' : 'auto'
                                  }}
                                >
                                  <div className={`font-medium text-tebox-ink`} style={{ fontSize: isMobile ? '15px' : '14px' }}>
                                    {pass.passProduct?.name || 'Pass'}
                                  </div>
                                  {pass.passProduct?.category ? (
                                    <div className={`${isMobile ? 'text-tebox-body' : 'text-tebox-body'} text-tebox-muted`} style={{ fontSize: isMobile ? '13px' : '14px' }}>
                                      {pass.passProduct.category}
                                    </div>
                                  ) : null}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleApplyPass}
                          disabled={!selectedPass || !selectedTeeTime}
                          className={`${isMobile ? 'w-full px-6 py-3.5 text-tebox-body' : 'px-6 py-3 text-tebox-body'} font-medium rounded-[6px] transition-colors disabled:cursor-not-allowed`}
                          style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: '500',
                            fontSize: isMobile ? '15px' : '14px',
                            minHeight: isMobile ? '44px' : 'auto',
                            ...(selectedPass
                              ? {
                                  backgroundColor: brandColors.buttonColor,
                                  color: brandColors.primaryTextColor,
                                  border: 'none',
                                  opacity: !selectedTeeTime ? 0.5 : 1,
                                }
                              : {
                                  backgroundColor: '#ffffff',
                                  color: brandColors.textColor,
                                  border: `1px solid ${tebox.colors.border}`,
                                }),
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    )}
                    {appliedPass && (
                      <div
                        style={{
                          padding: '16px',
                          borderRadius: 'var(--brand-border-radius, 6px)',
                          boxSizing: 'border-box',
                          ...reservationSummaryBrandChrome,
                        }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div
                              className="truncate font-medium"
                              style={{
                                fontFamily: bookingBrandFontFamilyVar,
                                fontSize: isMobile ? '13px' : '14px',
                                color: reservationSummaryAdjustedPriceColor,
                              }}
                            >
                              {appliedPass.passProduct?.name || 'Pass applied'} -
                            </div>
                            <div
                              className={isMobile ? 'text-tebox-body' : 'text-tebox-body'}
                              style={{
                                fontFamily: bookingBrandFontFamilyVar,
                                fontSize: isMobile ? '12px' : '14px',
                                color: reservationSummaryAdjustedPriceColor,
                              }}
                            >
                              Discount: ${passDiscount.toFixed(2)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemovePass}
                            className={`shrink-0 transition-opacity hover:opacity-80 ${isMobile ? 'text-tebox-body px-3 py-2' : 'text-tebox-body'}`}
                            style={{
                              fontFamily: bookingBrandFontFamilyVar,
                              fontSize: isMobile ? '14px' : '14px',
                              minHeight: isMobile ? '36px' : 'auto',
                              minWidth: isMobile ? '60px' : 'auto',
                              color: reservationSummaryAdjustedPriceColor,
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    )}
                    {availablePasses.length === 0 && authenticatedCustomer && (
                      <div style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontSize: teboxDesignSystem.typography.caption.fontSize,
                        color: teboxDesignSystem.colors.text.secondary,
                        fontStyle: 'italic'
                      }}>
                        No active passes found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Coupons — own section (card) */}
            <section
              className={`rounded-[6px] ${isMobile ? 'space-y-2 p-4' : 'space-y-3 p-5'}`}
              style={{
                ...checkoutAuxiliarySectionSurfaceStyle,
                border: reservationSummaryBrandChrome.border,
              }}
              aria-labelledby="checkout-coupons-heading"
            >
              <div className="flex items-center gap-2">
                <CouponTagIcon
                  size={isMobile ? 20 : 22}
                  color={brandColors.textColor}
                  strokeWidth={ICON_STROKE.standard}
                />
                <h3
                  id="checkout-coupons-heading"
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontSize: isMobile ? '15px' : '16px',
                    lineHeight: isMobile ? '22px' : '24px',
                    color: brandColors.textColor,
                    margin: 0
                  }}
                >
                  Coupons
                </h3>
              </div>
              
              {/* Available Coupons Dropdown */}
              {availableCoupons.length > 0 && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPassDropdown(false);
                      setShowCouponDropdown(!showCouponDropdown);
                    }}
                    className={`w-full ${isMobile ? 'px-4 py-3.5 text-tebox-body' : 'px-4 py-3 text-tebox-body'} border border-tebox-border rounded-[6px] text-left focus:outline-none focus:border-black bg-white`}
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: isMobile ? '15px' : '14px',
                      color: brandColors.textColor,
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    {showCouponDropdown ? 'Hide available coupons' : `View ${availableCoupons.length} available coupons`}
                  </button>
                  
                  {showCouponDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-tebox-border rounded-[6px] shadow-lg z-10 max-h-48 overflow-y-auto">
                      {availableCoupons.map((coupon) => (
                        <button
                          key={coupon.id}
                          onClick={() => handleSelectCoupon(coupon)}
                          className={`w-full ${isMobile ? 'px-4 py-3.5' : 'px-4 py-3'} text-left hover:bg-tebox-hover border-b border-tebox-border last:border-b-0`}
                          style={{
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: '400',
                            fontSize: isMobile ? '15px' : '14px',
                            minHeight: isMobile ? '44px' : 'auto'
                          }}
                        >
                          <div className={`font-medium text-tebox-ink`} style={{ fontSize: isMobile ? '15px' : '14px' }}>{coupon.code}</div>
                          <div className={`${isMobile ? 'text-tebox-body' : 'text-tebox-body'} text-tebox-muted`} style={{ fontSize: isMobile ? '13px' : '14px' }}>{coupon.name}</div>
                          {coupon.type === 'percentage' && (
                            <div className={`${isMobile ? 'text-tebox-body' : 'text-tebox-body'} text-green-600`} style={{ fontSize: isMobile ? '13px' : '14px' }}>{coupon.value}% off</div>
                          )}
                          {coupon.type === 'fixed_amount' && (
                            <div className={`${isMobile ? 'text-tebox-body' : 'text-tebox-body'} text-green-600`} style={{ fontSize: isMobile ? '13px' : '14px' }}>${coupon.value} off</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Manual Code Entry */}
              <div className={`flex ${isMobile ? 'flex-col' : ''} ${isMobile ? 'gap-2' : 'gap-3'}`}>
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setCouponApplicabilityHint(null);
                  }}
                  className={`flex-1 ${isMobile ? 'px-4 py-3.5 text-tebox-body' : 'px-4 py-3 text-tebox-body'} border border-tebox-border rounded-[6px] focus:outline-none focus:border-black`}
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontStyle: 'normal',
                    fontWeight: '400',
                    fontSize: isMobile ? '15px' : '14px',
                    minHeight: isMobile ? '44px' : 'auto'
                  }}
                />
                {promoCode.trim().length > 0 &&
                  (!appliedCoupon ||
                    appliedCoupon.code.trim().toLowerCase() !== promoCode.trim().toLowerCase()) && (
                  <button 
                    type="button"
                    onClick={handleApplyCoupon}
                    disabled={!selectedTeeTime}
                    className={`${isMobile ? 'w-full px-6 py-3.5 text-tebox-body' : 'px-6 py-3 text-tebox-body'} font-medium rounded-[6px] transition-colors disabled:bg-tebox-disabledBg disabled:text-tebox-disabledText disabled:cursor-not-allowed`}
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '500',
                      fontSize: isMobile ? '15px' : '14px',
                      backgroundColor: brandColors.buttonColor,
                      color: brandColors.primaryTextColor,
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    Apply
                  </button>
                )}
              </div>

              {/* Applied Coupon Display */}
              {appliedCoupon && (
                <div
                  style={{
                    padding: '16px',
                    borderRadius: 'var(--brand-border-radius, 6px)',
                    boxSizing: 'border-box',
                    ...reservationSummaryBrandChrome,
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        className="font-medium truncate"
                        style={{
                          fontFamily: bookingBrandFontFamilyVar,
                          fontSize: isMobile ? '13px' : '14px',
                          color: reservationSummaryAdjustedPriceColor,
                        }}
                      >
                        {appliedCoupon.code} - {appliedCoupon.name}
                      </div>
                      <div
                        className={isMobile ? 'text-tebox-body' : 'text-tebox-body'}
                        style={{
                          fontFamily: bookingBrandFontFamilyVar,
                          fontSize: isMobile ? '12px' : '14px',
                          color: reservationSummaryAdjustedPriceColor,
                        }}
                      >
                        Discount: ${couponDiscount.toFixed(2)}
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      type="button"
                      className={`shrink-0 transition-opacity hover:opacity-80 ${isMobile ? 'text-tebox-body px-3 py-2' : 'text-tebox-body'}`}
                      style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontSize: isMobile ? '14px' : '14px',
                        minHeight: isMobile ? '36px' : 'auto',
                        minWidth: isMobile ? '60px' : 'auto',
                        color: reservationSummaryAdjustedPriceColor,
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}

              {/* Validation Error */}
              {couponValidationResult && !couponValidationResult.isValid && (
                <div className={`${isMobile ? 'p-2.5' : 'p-3'} bg-red-50 border border-red-200 rounded-[6px]`}>
                  <div className={`text-red-800 ${isMobile ? 'text-tebox-body' : 'text-tebox-body'}`} style={{ fontFamily: bookingBrandFontFamilyVar, fontSize: isMobile ? '13px' : '14px' }}>
                    {couponValidationResult.errorMessage}
                  </div>
                </div>
              )}

              {couponApplicabilityHint ? (
                <div
                  className={`flex items-start gap-3 ${isMobile ? 'p-3' : 'p-3'} rounded-[6px] border border-amber-200 bg-amber-50`}
                  role="status"
                  aria-live="polite"
                >
                  <AlertTriangle
                    className="shrink-0 text-amber-600"
                    size={isMobile ? 22 : 20}
                    strokeWidth={ICON_STROKE.standard}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div
                      className={`font-medium text-amber-950 ${isMobile ? 'text-tebox-body' : 'text-tebox-body'} mb-1`}
                      style={{ fontFamily: bookingBrandFontFamilyVar, fontSize: isMobile ? '14px' : '14px' }}
                    >
                      Offer not available for this booking
                    </div>
                    <div
                      className={`font-normal text-amber-900 ${isMobile ? 'text-tebox-body' : 'text-tebox-body'} leading-snug`}
                      style={{ fontFamily: bookingBrandFontFamilyVar, fontSize: isMobile ? '13px' : '14px' }}
                    >
                      {couponApplicabilityHint}
                    </div>
                  </div>
                </div>
              ) : null}
            </section>

            {/* Customer Message Input */}
            <div className={`${isMobile ? 'space-y-2' : 'space-y-3'}`}>
              <label className={`block ${isMobile ? 'text-tebox-body' : 'text-tebox-body'} font-medium text-tebox-ink`} style={{ fontFamily: bookingBrandFontFamilyVar, fontSize: isMobile ? '14px' : '14px' }}>
                Message for Staff (Optional)
              </label>
              <textarea
                value={customerMessage}
                onChange={(e) => setCustomerMessage(e.target.value)}
                placeholder="Any special requests or questions for the staff? (Optional)"
                maxLength={500}
                rows={isMobile ? 3 : 4}
                className={`w-full ${isMobile ? 'px-3 py-2.5 text-tebox-body' : 'px-3 py-2 text-tebox-body'} border border-tebox-border rounded-[6px] focus:outline-none focus:border-black resize-none`}
                style={{
                  fontFamily: bookingBrandFontFamilyVar,
                  fontSize: isMobile ? '15px' : '14px'
                }}
              />
              <div className={`flex justify-between items-center ${isMobile ? 'text-tebox-caption' : 'text-tebox-caption'} text-tebox-muted`} style={{ fontFamily: bookingBrandFontFamilyVar, fontSize: isMobile ? '12px' : '12px' }}>
                <span>
                  {authenticatedCustomer 
                    ? "Message will be sent to staff" 
                    : "Log in to send messages to staff"
                  }
                </span>
                <span>{customerMessage.length}/500 characters</span>
              </div>
              {!authenticatedCustomer && customerMessage.trim() && (
                <div className={`${isMobile ? 'text-tebox-caption p-2' : 'text-tebox-caption p-2'} text-amber-600 bg-amber-50 rounded border border-amber-200`} style={{ fontFamily: bookingBrandFontFamilyVar, fontSize: isMobile ? '12px' : '12px' }}>
                  ⚠️ You need to log in to send messages to staff. Your reservation will be created, but your message won't be sent.
                </div>
              )}
            </div>

            {/* Payment step (prepay / card on file) */}
            {checkoutIntentData &&
            checkoutPaymentRequirement &&
            onlinePaymentRequirementNeedsCheckout(checkoutPaymentRequirement) &&
            !paymentCompleted ? (
              <div className={`${isMobile ? 'space-y-2' : 'space-y-3'} border border-tebox-border rounded-[6px] p-4`}>
                <label
                  className={`block ${isMobile ? 'text-tebox-body' : 'text-tebox-body'} font-medium text-tebox-ink`}
                  style={{ fontFamily: bookingBrandFontFamilyVar, fontSize: isMobile ? '14px' : '14px' }}
                >
                  Payment required
                </label>
                <OnlineBookingPaymentStep
                  intent={checkoutIntentData}
                  amountCents={checkoutPaymentRequirement.prepaymentAmountCents}
                  onSuccess={({ paymentIntentId, setupIntentId }) => {
                    setOnlinePaymentIntentId(paymentIntentId);
                    setOnlineSetupIntentId(setupIntentId);
                    setPaymentCompleted(true);
                    setPaymentStepError(null);
                  }}
                  onError={(message) => setPaymentStepError(message)}
                  disabled={isCreatingReservation}
                />
                {paymentStepError ? (
                  <p className="text-tebox-caption text-red-700">{paymentStepError}</p>
                ) : null}
                {paymentCompleted ? (
                  <p className="text-tebox-caption text-green-800">Payment saved — click Confirm Reservation to finish.</p>
                ) : null}
              </div>
            ) : null}

            {/* Checkout Button */}
            <div className="pt-4">
              {!authenticatedCustomer ? (
                <div className="space-y-3">
                  <div className={`text-center ${isMobile ? 'p-3' : 'p-4'} bg-amber-50 border border-amber-200 rounded-[6px]`}>
                    <div className={`text-amber-800 font-medium ${isMobile ? 'mb-2 text-tebox-body' : 'mb-2'}`} style={{ fontFamily: bookingBrandFontFamilyVar, fontSize: isMobile ? '15px' : '14px' }}>🔐 Login Required</div>
                    <div className={`${isMobile ? 'text-tebox-body mb-3' : 'text-tebox-body mb-3'} text-amber-700`} style={{ fontFamily: bookingBrandFontFamilyVar, fontSize: isMobile ? '13px' : '14px' }}>
                      You must be logged in to make reservations
                    </div>
                    <button
                      onClick={() => setShowLoginModal(true)}
                      className={`w-full ${isMobile ? 'py-3.5 px-6 text-tebox-body' : 'py-3 px-6 text-tebox-body'} bg-amber-600 text-white rounded-[6px] font-medium hover:bg-amber-700 transition-colors`}
                      style={{ 
                        fontFamily: bookingBrandFontFamilyVar,
                        fontSize: isMobile ? '15px' : '14px',
                        minHeight: isMobile ? '44px' : 'auto'
                      }}
                    >
                      Log In to Continue
                    </button>
                  </div>
                </div>
              ) : (
                <button 
                  onClick={handleConfirmReservation}
                  disabled={isCreatingReservation}
                  className={`w-full ${isMobile ? 'py-4 px-6 text-tebox-h4' : 'py-4 px-6 text-tebox-body'} rounded-[6px] font-medium transition-colors ${
                    isCreatingReservation 
                      ? 'bg-tebox-disabledBg text-tebox-disabledText cursor-not-allowed' 
                      : ''
                  }`}
                  style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontSize: isMobile ? '17px' : '16px',
                    backgroundColor: isCreatingReservation ? tebox.colors.textTertiary : brandColors.buttonColor,
                    color: isCreatingReservation ? tebox.colors.border : brandColors.primaryTextColor,
                    minHeight: isMobile ? '52px' : 'auto'
                  }}>
                  {isCreatingReservation ? 'Creating Reservation...' : 'Confirm Reservation'}
                </button>
              )}
              
              {/* Error Display */}
              {reservationError && (
                <div className={`mt-3 ${isMobile ? 'p-2.5' : 'p-3'} bg-red-50 border border-red-200 rounded ${isMobile ? 'text-tebox-body' : 'text-tebox-body'} text-red-700`} style={{ fontSize: isMobile ? '13px' : '14px' }}>
                  {reservationError}
                </div>
              )}
              
            </div>

            {/* Club Policies */}
            <div className={isMobile ? 'pt-4' : 'pt-6'}>
              <h3 style={{
                fontFamily: bookingBrandFontFamilyVar,
                fontStyle: 'normal',
                fontWeight: '500',
                fontSize: isMobile ? '15px' : '16px',
                color: brandColors.textColor,
                marginBottom: isMobile ? '10px' : '14px'
              }}>Club Policies</h3>
              <div style={{
                fontFamily: bookingBrandFontFamilyVar,
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: isMobile ? '13px' : '14px',
                lineHeight: isMobile ? '18px' : '20px',
                color: brandColors.secondaryColor,
                whiteSpace: 'pre-wrap'
              }}>
                {clubDetails?.policies ? clubDetails.policies : 'No policies found'}
              </div>

            </div>
            </div>
          </div>
          </div>
        </div>

        {/* Footer - Powered by tebox */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '24px 16px' : '32px',
            backgroundColor: tebox.colors.bgPrimary,
            borderTop: `1px solid ${tebox.colors.border}`,
            marginTop: 'auto'
          }}
        >
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: isMobile ? '12px' : '13px',
              color: brandColors.secondaryColor || tebox.colors.textSecondary
            }}
          >
            <span>Powered by</span>
            <img 
              src={TEBOX_DEFAULT_LOGO_URL} 
              alt="tebox" 
              style={{
                height: isMobile ? '19px' : '22px',
                width: 'auto',
                maxWidth: isMobile ? '84px' : '96px',
                objectFit: 'contain'
              }}
            />
          </div>
        </div>

        {/* Reservation Confirmation Modal - Only on confirmation page */}
        <ReservationConfirmationModal
          isOpen={showConfirmationModal}
          onClose={handleConfirmationModalClose}
          reservationDetails={reservationDetails || undefined}
          partnerInvite={{
            inviteUrl: partnerInviteUrl,
            loading: partnerInviteLoading,
            error: partnerInviteError,
            canInvite: Boolean(
              authenticatedCustomer &&
                reservationDetails?.reservationId &&
                (reservationDetails.players ?? 0) > 1
            ),
            onMint: handleMintPartnerInvite,
          }}
          brandColors={{
            fontFamily: brandColors.fontFamily,
            textColor: brandColors.textColor,
            primaryColor: brandColors.primaryColor,
            buttonColor: brandColors.buttonColor,
            buttonTextColor: brandColors.buttonTextColor,
            primaryTextColor: brandColors.primaryTextColor,
          }}
        />
      </div>
    );
  }

  const bookingAccountMenu = (
    <BookingAccountMenu
      authenticatedCustomer={authenticatedCustomer}
      brandColors={brandColors}
      isMobile={isMobile}
      onSignIn={() => {
        setShowLoginModal(true);
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('setLoginModalTab', { detail: { tab: 0 } }),
          );
        }, 100);
      }}
      onLogout={async () => {
        await customerAuthService.logout();
        setAuthenticatedCustomer(null);
        setShowDashboard(false);
      }}
      showDashboard={showDashboard}
      onOpenDashboard={() => setShowDashboard(true)}
      onCloseDashboard={() => setShowDashboard(false)}
    />
  );

  // Dashboard view — scroll inside viewport shell (reliable vs. document scroll)
  if (showDashboard) {
    const dashboardWelcomeShort =
      authenticatedCustomer?.firstName?.split('.')[0]?.split(' ')[0] ||
      authenticatedCustomer?.firstName?.trim() ||
      'Guest';

    console.log('🔍 Rendering GolferDashboard component');
    return (
      <div className="golfer-dashboard-scroll-shell">
        {isMobile && authenticatedCustomer ? (
          <div
            className="sticky top-0 bg-white border-b border-tebox-border px-4 py-3 shrink-0 relative"
            style={{ zIndex: 110 }}
          >
            <div className="flex items-center justify-between gap-3 min-w-0">
              <h1
                style={{
                  fontFamily: bookingBrandFontFamilyVar,
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: 20,
                  lineHeight: '31.5px',
                  letterSpacing: 'normal',
                  color: brandColors.textColor,
                  margin: 0,
                  minWidth: 0,
                  flex: '1 1 auto',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {`Hello, ${dashboardWelcomeShort}`}
              </h1>
              <div className="shrink-0">{bookingAccountMenu}</div>
            </div>
          </div>
        ) : null}
        <GolferDashboard
          onBackToBooking={() => setShowDashboard(false)}
          brandColors={brandColors}
          omitWelcomeHeading={Boolean(isMobile && authenticatedCustomer)}
        />
      </div>
    );
  }

  // Show error if clubId or clubNumber is missing (unless finishing Google/Apple OAuth return).
  if (!clubIdOrNumber && !isOAuthReturn) {
    return (
      <div className="h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="bg-white border border-tebox-border rounded-[6px] p-8 max-w-md text-center">
          <h2 style={{
            fontFamily: bookingBrandFontFamilyVar,
            fontStyle: 'normal',
            fontWeight: '500',
            fontSize: teboxDesignSystem.typography.h3.fontSize,
            lineHeight: '21px',
            color: brandColors.textColor,
            marginBottom: '16px'
          }}>Club ID Required</h2>
          <p style={{
            fontFamily: bookingBrandFontFamilyVar,
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: teboxDesignSystem.typography.body.fontSize,
            color: brandColors.secondaryColor,
            marginBottom: '24px'
          }}>
            The booking engine requires a club ID to function. Please access this page with a valid club ID in the URL.
          </p>
          <p style={{
            fontFamily: bookingBrandFontFamilyVar,
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: teboxDesignSystem.typography.caption.fontSize,
            color: tebox.colors.textTertiary
          }}>
            Example: /online-booking?clubNumber=1 or /online-booking?clubId=your-club-id
          </p>
        </div>
      </div>
    );
  }

  if (!clubIdOrNumber && isOAuthReturn) {
    return (
      <>
        <div
          className="flex flex-col items-center justify-center p-6"
          style={{
            minHeight: isMobile ? '-webkit-fill-available' : '100vh',
            backgroundColor: tebox.colors.bgPrimary,
          }}
        >
          <p
            style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: teboxDesignSystem.typography.body.fontSize,
              color: brandColors.secondaryColor,
              margin: 0,
            }}
          >
            Completing sign-in…
          </p>
        </div>
        <LoginRegisterModal
          open={showLoginModal}
          onClose={handleLoginModalClose}
          onSuccess={handleAuthenticationSuccess}
          brandColors={brandColors}
          {...oauthReturnClubAuth}
        />
      </>
    );
  }

  // Main search view
  return (
    <>
    {clubIdOrNumber && coursesLoading && !coursesError ? (
      <div
        className="flex items-center justify-center p-6"
        style={{ minHeight: isMobile ? '-webkit-fill-available' : '100vh', backgroundColor: tebox.colors.bgPrimary }}
      >
        <div
          style={{
            fontFamily: bookingBrandFontFamilyVar,
            fontSize: teboxDesignSystem.typography.body.fontSize,
            color: brandColors.secondaryColor,
            textAlign: 'center',
          }}
        >
          Loading course and booking options…
        </div>
      </div>
    ) : null}
    {clubIdOrNumber && coursesError ? (
      <div
        className="flex flex-col items-center justify-center p-6"
        style={{ minHeight: isMobile ? '-webkit-fill-available' : '100vh', backgroundColor: tebox.colors.bgPrimary }}
      >
        <div
          className="max-w-md rounded-[6px] border border-tebox-border p-6 text-center"
          style={{ backgroundColor: teboxDesignSystem.colors.background.primary }}
        >
          <h2
            style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontWeight: 500,
              fontSize: teboxDesignSystem.typography.h3.fontSize,
              color: brandColors.textColor,
              margin: `0 0 ${teboxDesignSystem.spacing.md} 0`,
            }}
          >
            Cannot reach the booking server
          </h2>
          <p
            style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: teboxDesignSystem.typography.body.fontSize,
              color: brandColors.secondaryColor,
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {coursesError instanceof Error
              ? coursesError.message
              : 'Check that the API is running and that you opened this page with a valid ?clubId= or ?clubNumber= link.'}
          </p>
        </div>
      </div>
    ) : null}
    {!(clubIdOrNumber && (coursesLoading || coursesError)) ? (
    <div 
      className="bg-white flex flex-col"
      style={{
        minHeight: isMobile ? '-webkit-fill-available' : '100vh',
        height: isMobile ? 'auto' : '100vh',
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none'
      }}
    >
      {/* Main Header */}
      <div className={`bg-white ${shopCatalog?.shopEnabled ? '' : 'border-b border-tebox-border'} ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} relative`}>
        <div className="flex items-center justify-between">
          {/* Club Name with Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {brandSettingsData?.logoUrl && (
              <img 
                src={brandSettingsData.logoUrl} 
                alt={clubDetails?.name || 'Club Logo'} 
                style={{
                  width: isMobile ? '32px' : '40px',
                  height: isMobile ? '32px' : '40px',
                  objectFit: 'contain'
                }}
              />
            )}
            <h2 style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontStyle: 'normal',
              fontWeight: '500',
              fontSize: isMobile ? '20px' : '24px',
              lineHeight: '21px',
              color: brandColors.textColor,
              margin: 0
            }}>{clubDetails?.name || 'Golf Club'}</h2>
          </div>

          {bookingAccountMenu}
        </div>
      </div>

      {shopCatalog?.shopEnabled ? (
        <BookingSubNav
          active="book"
          clubRouteKey={clubIdOrNumber}
          textColor={brandColors.textColor}
          accentColor={brandColors.buttonColor}
          isMobile={isMobile}
        />
      ) : null}

      {/* Search Form — desktop: hides while scrolling results to free vertical space; mobile unchanged */}
      <div
        className={
          isMobile
            ? 'px-4 py-3'
            : 'relative z-40 px-6'
        }
      >
        <div
          style={
            isMobile
              ? undefined
              : {
                  maxHeight: bookingDesktopScrollAwayChromeOpen ? 1200 : 0,
                  opacity: bookingDesktopScrollAwayChromeOpen ? 1 : 0,
                  /* When open: allow date/course popovers to extend over results; when collapsed: clip for height animation */
                  overflow: bookingDesktopScrollAwayChromeOpen ? 'visible' : 'hidden',
                  transition: 'max-height 0.35s ease, opacity 0.22s ease',
                  paddingTop: bookingDesktopScrollAwayChromeOpen ? teboxDesignSystem.spacing.md : 0,
                  paddingBottom: bookingDesktopScrollAwayChromeOpen ? teboxDesignSystem.spacing.md : 0,
                  pointerEvents: bookingDesktopScrollAwayChromeOpen ? 'auto' : 'none',
                }
          }
        >
          <BookingSearchForm
            onSearch={handleSearch}
            isLoading={isLoading}
            courses={courses}
            selectedCourseIds={searchCourses}
            onCourseChange={setSearchCourses}
            selectedTimeOfDay={searchTimeOfDay}
            onTimeOfDayChange={(value) => setSearchTimeOfDay(value as 'AM' | 'PM' | 'Both')}
            selectedRoundType={searchHoles === 'Both' ? '18-hole' : `${searchHoles}-hole`}
            onRoundTypeChange={(value) => setSearchHoles(value === '18-hole' ? '18' : '9')}
            brandColors={{
              primaryColor: brandColors.primaryColor,
              secondaryColor: brandColors.secondaryColor,
              textColor: brandColors.textColor,
              fontFamily: brandColors.fontFamily,
              buttonColor: brandColors.buttonColor,
              buttonTextColor: brandColors.buttonTextColor,
              primaryTextColor: brandColors.primaryTextColor
            }}
          />
        </div>
      </div>

      {/* Results Header — desktop: same scroll-away behavior as search filters */}
      <div className={`${isMobile ? 'px-4 py-0' : 'px-6 py-0'}`}>
        <div
          style={
            isMobile
              ? undefined
              : {
                  maxHeight: bookingDesktopScrollAwayChromeOpen ? 800 : 0,
                  opacity: bookingDesktopScrollAwayChromeOpen ? 1 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.35s ease, opacity 0.22s ease',
                  pointerEvents: bookingDesktopScrollAwayChromeOpen ? 'auto' : 'none',
                }
          }
        >
          <h2
            style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontStyle: 'normal',
              fontWeight: '500',
              fontSize: isMobile ? '20px' : '22px',
              lineHeight: '21px',
              color: brandColors.textColor,
              paddingTop: '5px',
              paddingBottom: '5px',
              margin: 0,
            }}
          >
            Available Times
          </h2>
        </div>
      </div>

      {/* Results — z-0 so filter popovers (search chrome z-40) paint above the tee grid when they overlap */}
      <div
        className={`${isMobile ? 'px-4 pb-4' : 'px-6 pb-6'} relative z-0 flex-1 min-h-0 overflow-hidden`}
      >
        {isLoading ? (
          <div className="text-center py-12">
            <div style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              color: tebox.colors.textPrimary
            }}>Searching for available tee times...</div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              color: '#EF4444'
            }}>Error loading tee times. Please try again.</div>
            <div style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              color: tebox.colors.textTertiary
            }} className="mt-2">
              Date: {searchDate.toLocaleDateString()} | Players: {searchPlayers}
            </div>
          </div>
        ) : teeTimes.length === 0 ? (
          <div className="text-center py-12">
            <div style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              color: tebox.colors.textPrimary
            }}>No tee times available for the selected criteria.</div>
            <div style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              color: tebox.colors.textSecondary
            }} className="mt-2">
              Date: {searchDate.toLocaleDateString()} | Players: {searchPlayers}
            </div>
          </div>
        ) : (
          <div className="space-y-4 h-full flex flex-col">
            <div
              style={
                isMobile
                  ? undefined
                  : {
                      maxHeight: bookingDesktopScrollAwayChromeOpen ? 1200 : 0,
                      opacity: bookingDesktopScrollAwayChromeOpen ? 1 : 0,
                      overflow: 'hidden',
                      transition: 'max-height 0.35s ease, opacity 0.22s ease',
                      pointerEvents: bookingDesktopScrollAwayChromeOpen ? 'auto' : 'none',
                    }
              }
            >
              <div
                className={
                  isMobile
                    ? 'flex w-full flex-col gap-3'
                    : 'flex w-full flex-row flex-wrap items-center justify-between gap-4'
                }
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    flexWrap: 'wrap',
                    minWidth: 0,
                    flex: isMobile ? undefined : 1,
                  }}
                >
                  <div
                    style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      color: tebox.colors.textPrimary,
                    }}
                  >
                    Found {teeTimes.length} available tee time{teeTimes.length === 1 ? '' : 's'} for {searchPlayers}{' '}
                    {searchPlayers === 1 ? 'player' : 'players'} on {searchDate.toLocaleDateString()} for{' '}
                    {searchHoles === 'Both' ? '18 holes' : `${searchHoles} holes`} on{' '}
                    {searchCourses.length === 0
                      ? 'all courses'
                      : searchCourses.length === 1
                        ? '1 course'
                        : `${searchCourses.length} courses`}
                    {bookingMemo?.content && ` · ${bookingMemo.content}`}
                  </div>

                  {/* Course Legend - Only visible in tee sheet view */}
                  {viewMode === 'teesheet' && courses.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {courses.map((course) => (
                        <div
                          key={course.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: course.color || tebox.colors.textSecondary,
                            }}
                          />
                          <span
                            style={{
                              fontFamily: bookingBrandFontFamilyVar,
                              fontStyle: 'normal',
                              fontWeight: '400',
                              fontSize: teboxDesignSystem.typography.body.fontSize,
                              color: tebox.colors.textSecondary,
                            }}
                          >
                            {course.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* View toggle: same row as summary on desktop */}
                {isMemberResult && (
                  <div className={`flex shrink-0 items-center gap-2 ${isMobile ? 'justify-start' : ''}`}>
                    <button
                      type="button"
                      onClick={() => setViewMode('grid')}
                      style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontStyle: 'normal',
                        fontWeight: '500',
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        color: viewMode === 'grid' ? brandColors.textColor : tebox.colors.textSecondary,
                        backgroundColor: 'transparent',
                        border: 'none',
                        padding: '8px 14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textDecoration: viewMode === 'grid' ? 'underline' : 'none',
                      }}
                    >
                      Grid
                    </button>
                    <span
                      style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        color: tebox.colors.textSecondary,
                      }}
                    >
                      |
                    </span>
                    <button
                      type="button"
                      onClick={() => setViewMode('teesheet')}
                      style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontStyle: 'normal',
                        fontWeight: '500',
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        color: viewMode === 'teesheet' ? brandColors.textColor : tebox.colors.textSecondary,
                        backgroundColor: 'transparent',
                        border: 'none',
                        padding: '8px 14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textDecoration: viewMode === 'teesheet' ? 'underline' : 'none',
                      }}
                    >
                      Tee Sheet
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Results Grid/List View */}
            <div
              ref={bookingResultsScrollElRef}
              className="flex-1 overflow-y-auto min-h-0"
              onScroll={onBookingResultsScroll}
            >
              <ResultsGrid
                teeTimes={teeTimes}
                isLoading={isLoading}
                error={error ? 'Error loading tee times' : null}
                onBook={handleTeeTimeBook}
                isBooking={false}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                gridPresentation={gridPresentation}
                isMember={teeSheetPlayerNamesVisible}
                selectedDate={searchDate}
                blockedTeeTimes={blockedTeeTimes}
                preloadHalfCartWithGreenFeeByCourseId={
                  preloadHalfCartWithGreenFeeByCourseId
                }
                brandColors={{
                  primaryColor: brandColors.primaryColor,
                  secondaryColor: brandColors.secondaryColor,
                  textColor: brandColors.textColor,
                  fontFamily: brandColors.fontFamily,
                  buttonColor: brandColors.buttonColor,
                  headingTextBold: brandColors.headingTextBold,
                  headingTextItalic: brandColors.headingTextItalic,
                }}
              />
            </div>
          </div>
        )}
        </div>
    </div>
    ) : null}

    {/* Per-player green fee modal (before checkout) */}
    <MultiPlayerPricingModal
      isOpen={showPerPlayerRatesModal}
      onClose={handlePerPlayerRatesModalClose}
      teeTime={
        selectedTeeTimeForPricing
          ? {
              courseName: selectedTeeTimeForPricing.courseName,
              dateTime: selectedTeeTimeForPricing.dateTime,
              originalDate: selectedTeeTimeForPricing.originalDate,
            }
          : null
      }
      playerCount={searchPlayers}
      pricingOptions={selectedTeeTimeForPricing?.pricingOptions || []}
      linkedPlayerSlots={selectedMembers.map((sm) => sm.playerNumber)}
      linkedSlotLabels={linkedSlotLabelsForModal}
      isMemberBooker={isMemberResult}
      defaultGuestCustomerTypeId={resolvedDefaultGuestCustomerTypeId}
      bookerCustomerTypeId={getCustomerTypeIdString(authenticatedCustomer)}
      initialCustomerTypeByPlayer={initialCustomerTypeByPlayerForModal}
      initNonce={perPlayerRatesModalNonce}
      onConfirm={handlePerPlayerRatesConfirm}
      isAuthenticated={!!authenticatedCustomer}
      bookerSlotLabel={bookerSlotLabelForModal}
      brandColors={{
        fontFamily: brandColors.fontFamily,
        textColor: brandColors.textColor,
        secondaryColor: brandColors.secondaryColor,
        primaryColor: brandColors.primaryColor,
        buttonColor: brandColors.buttonColor,
        buttonTextColor: brandColors.buttonTextColor,
      }}
    />

    {/* Login/Register Modal */}
    <LoginRegisterModal
      open={showLoginModal}
      onClose={handleLoginModalClose}
      onSuccess={handleAuthenticationSuccess}
      teeTimeInfo={selectedTeeTime ? {
        dateTime: selectedTeeTime.dateTime.toISOString(),
        courseName: selectedTeeTime.courseName,
        price: selectedTeeTime.price
      } : undefined}
      selectedCustomerTypeId={selectedTeeTime?.customerTypeId} // Pass selected customer type from rate selector
      brandColors={brandColors} // Pass club custom branding
      {...clubAuthFieldsFromBookingUrl(clubIdOrNumber, clubNameFromUrl)}
    />

    {/* Member Search Modal */}
    <MemberSearch
      isOpen={showMemberSearch}
      onClose={() => setShowMemberSearch(false)}
      onMemberSelect={handleMemberSelect}
      placeholder="Search for a member by name..."
      clubNumber={(() => {
        const d = decodeClubRouteKey(clubIdOrNumber ?? undefined);
        return d.clubNumber && /^\d+$/.test(d.clubNumber) ? Number(d.clubNumber) : undefined;
      })()}
      brandColors={{
        fontFamily: brandColors.fontFamily,
        textColor: brandColors.textColor,
        secondaryColor: brandColors.secondaryColor
      }}
    />

    </>
  );
};

export default OnlineBookingEngine;
